<template>
  <div id="editorMainDiv" class="overflow-hidden">
    <CreateWorkflowCommentModal
      ref="WorkflowCommentModalView"
      @note-updated="onNoteUpdated"
    />
    <menu
      id="wfd-description-menu"
      class="context-menu-default show-context-menu-default"
      style="display: none; position: fixed; padding-right: 0"
    >
      <li class="context-menu-default-item" @mouseleave="closeWFDContextMenu">
        <button
          class="btn btn-secondary w-100"
          @click="showCreateWorkflowCommentModal"
        >
          <i class="fa fa-plus-circle"></i>
          {{
            $t("workflowElements.descriptionElement.addNewDescriptionElement")
          }}
        </button>
      </li>
    </menu>
    <div id="konva" class="overflow-visible"></div>
    <div id="node-states"></div>
    <div
      id="konva-preview"
      class="border shadow bg-white position-fixed"
      style=""
    ></div>
    <transition name="fade">
      <div
        v-if="noteHovered && noteHovering"
        class="position-fixed bg-white rounded shadow p-5"
        style="transform: translate(-100%, -50%); max-width: 400px"
        :style="`top: ${notesToolTipPosition.y}px; left: ${notesToolTipPosition.x}px;`"
        @mouseenter="notesToolTipMouseEnter"
        @mousemove="notesToolTipMouseEnter"
        @mouseleave="notesToolTipMouseLeave"
      >
        <div>
          <b
            class="btn btn-icon btn-light btn-sm mr-1"
            @click="editNote(noteHovered.attrs.note)"
          >
            <i class="fal fa-pen"></i>
          </b>
          <b
            class="btn btn-icon btn-light btn-sm mr-1"
            @click="removeNoteFromView(noteHovered.attrs.note)"
          >
            <i class="fal fa-trash"></i>
          </b>
        </div>
      </div>
    </transition>
    <ContextMenu
      ref="contextMenu"
      :node="contextNode"
      :special-elements="specialElements"
      style="display: none"
      @end-point="toggleEndpoint"
      @copy="copyNode"
      @copyWithChildren="copyNodeWithChildren"
      @delete="deleteNode"
      @deactivate="deactivateNode"
      @open-php-editor="openPhpEditor"
      @close="onCloseContextMenu"
    />
    <Toolbar
      :toolbar-options="toolbarOptions"
      :history-step="historyStep"
      :history-length="history.length"
      @toggle-critical-path="toggleCriticalPath"
      @toggle-map="toggleMap"
      @center-view="centerView"
      @zoom="zoom"
      @share="share"
      @clear-all="clearAll"
      @undo="historyUndo"
      @redo="historyRedo"
    />
    <b-modal
      id="editor-modal"
      :modal-class="modalAttributes.class"
      :body-class="modalAttributes.bodyClass"
      :title="$t(modalAttributes.title ?? '')"
      hide-footer
      @close="modalComponent = undefined"
    >
      <component
        :is="modalComponent"
        :is-modal="true"
        :mock-data="true"
        v-bind="modalAttributes"
        @change="$emit('change')"
      />
    </b-modal>
  </div>
</template>

<script>
import FlowComments from "@/components/FlowComments/flowComments";
import CreateWorkflowCommentModal from "@/components/FlowComments/Modal.vue";
import $ from "jquery";
import "jquery-ui-dist/jquery-ui";
import Konva from "konva";
import {
  circleScene,
  dropzoneScene,
  workflowElementToData
} from "@/components/Workflows/Designer/Canvas/Components/editorHelpers";
import { bus } from "@/main";
import { generateHash } from "@/components/Tools/helperFunctions";
import ContextMenu from "@/components/Workflows/Designer/Canvas/Components/ContextMenu.vue";
import Toolbar from "@/components/Workflows/Designer/Canvas/Components/Toolbar.vue";

import {
  ADD_CLIPBOARD_ELEMENT,
  GET_CLIPBOARD_ELEMENTS
} from "@/core/services/store/workflowDesigner.module";
import CodeFieldHelper from "@/components/Workflows/Designer/Canvas/Components/FieldHelperFields/Code.vue";

import { formatDate } from "@/components/Tools/helperFunctions";
import { mapActions, mapGetters } from "vuex";
import { SET_CUSTOM_VARIABLES } from "@/core/services/store/variables_v1.module";
import {
  addEventToLoadingQueue,
  removeEventFromLoadingQueue
} from "@/composables/useLoadingQueue";
import FlowElements from "@/components/Workflows/Designer/flowElements";
import { Toast } from "@/core/plugins/swal";
import { resolveLocalizedString } from "@/composables/useLocalizedDescription";

export default {
  name: "WFDEditor",
  components: {
    ContextMenu,
    Toolbar,
    CreateWorkflowCommentModal
  },
  props: ["library", "workflow", "process", "system-elements"],
  data() {
    return {
      notesFields: [
        {
          key: "id",
          label: this.$t("table.id"),
          sortable: true,
          thStyle: "width: 70px"
        },
        {
          key: "title",
          label: this.$t("table.title"),
          sortable: true
        },
        {
          key: "actions",
          label: this.$t("table.actions"),
          sortable: false,
          thStyle: { width: "170px" }
        }
      ],
      keysToDelete: ["hash", "group_hash", "next_hash", "x", "y"],
      noteResizeTimeout: null,
      noteTooltipTimeout: null,
      notesToolTipPosition: {
        x: 0,
        y: 0
      },
      newWFDDescriptionPositions: {
        top: 0,
        left: 0
      },
      showNewWFDDescriptionModal: false,
      notes: [],
      notesMeta: {
        page: 1,
        perPage: 100
      },
      noteHovered: false,
      noteHovering: false,
      // Konva variables
      stage: undefined,
      layers: {
        main: undefined,
        shadows: undefined
      },
      helperNodes: {
        clone: undefined,
        triggerShadow: undefined,
        descriptions: undefined
      },
      container: {
        width: 0,
        height: 0
      },
      grid: {
        width: 50,
        height: 25
      },
      nodes: [],
      currentNode: undefined,
      hoverNode: undefined,
      contextNode: undefined,
      lastDropResult: false,
      operatorGroupSelector: ".branch-group, .loop-group, .exception-group",
      operatorElementIds: [25, 28],
      history: [],
      historyStep: 0,
      specialElements: [
        "branch",
        "loop",
        "cache",
        "csvBatch",
        "xmlBatch",
        "xlsxBatch"
      ],
      // Helpful to separate groups of elements from other like groups with lines
      nodeGroups: [
        "node-group",
        "branch-group",
        "loop-group",
        "exception-group"
      ],
      // Preview
      preview: {
        stage: undefined,
        layer: undefined,
        layerRect: undefined
      },
      lastDropzone: undefined,
      dropzoneTimer: undefined,
      // Modal
      modalComponent: undefined,
      modalAttributes: {},
      // Meta
      isLoadingCanvas: true,
      tooltipOptions: {
        show: false,
        hovering: false,
        position: {},
        timeout: undefined,
        edit: false
      },
      tooltipKey: 1,
      toolbarOptions: {
        showCriticalPath: false,
        showMap: false,
        scale: 1
      },
      mouse: {
        x: 0,
        y: 0
      },
      colors: {
        primary: "#5b64ee",
        black: "#9babbd",
        white: "#ffffff",
        green: "#0BB7AF",
        greenLight: "#1BC5BD",
        gray: "#DDDDDD",
        red: "#F64E60"
      },
      resizeTimer: undefined,
      // Demo
      // demo: JSON.parse(JSON.stringify(demo))

      preventNodeClick: false
    };
  },
  computed: {
    ...mapGetters("variables", ["customVariablesSet"]),
    stageWidth() {
      return this.stage ? this.stage.width() : 0;
    },
    stageHeight() {
      return this.stage ? this.stage.height() : 0;
    },
    triggerShadow() {
      return this.helperNodes.triggerShadow;
    },
    descriptions() {
      return this.helperNodes.descriptions;
    },
    clone() {
      return this.helperNodes.clone;
    },
    triggers() {
      return this.layers.main
        ? this.layers.main.findOne("#trigger-group")
        : undefined;
    },
    mouseOverCanvas: function () {
      if (!this.stage) return false;
      let canvas = $(".konvajs-content")[0];
      if (!canvas) return false;
      let canvasPos = canvas.getBoundingClientRect();
      if (
        this.mouse.x > canvasPos.x &&
        this.mouse.x < canvasPos.x + canvasPos.width &&
        this.mouse.y > canvasPos.y &&
        this.mouse.y < canvasPos.y + canvasPos.height
      ) {
        return {
          x: (this.mouse.x - canvasPos.x) * (1 / this.scale),
          y: (this.mouse.y - canvasPos.y) * (1 / this.scale)
        };
      }
      return false;
    },
    scale: function () {
      return this.toolbarOptions.scale;
    },
    uniqueProcessIdentifier() {
      return `${this.process.classname}-${this.process.id}`;
    },
    systemWorkflowElementsIcons() {
      let returnData = [];
      this.library.forEach(element => {
        returnData[element.id] = element.icon;
      });
      return returnData;
    },
    stageCenter() {
      return (
        (5 * Math.floor(this.stageWidth / this.grid.width) * this.grid.width) /
          12 -
        this.grid.width / 2
      );
    }
  },
  watch: {
    currentNode: function () {
      bus.$emit("activeNodeChanged", this.currentNode);
    },
    nodes: function () {
      bus.$emit("nodesChanged", this.nodes);
    },
    noteHovered: function () {
      this.setNotesTooltipPosition();
    }
  },
  created() {
    this.$root.$refs.WFDEditor = this;
  },
  mounted() {
    this.currentNode = undefined;
    bus.$emit("activeNodeChanged", undefined);
    this.init();
    this.subscribeBusEvents();
    this.windowEvents();
    this.loadCanvas();
    this.$refs.WorkflowCommentModalView.note.workflow_id =
      this.workflow.id ?? "";
  },
  beforeDestroy() {
    if (this.stage) this.stage.destroy();
    this.unsubscribeBusEvents();
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    ...mapActions("variables", [SET_CUSTOM_VARIABLES]),
    onNoteUpdated(note) {
      if (note.workflow_id !== this.workflow.id) {
        return;
      }
      const noteElements = this.layers.descriptions
        .getChildren()
        .filter(x => x.attrs.note.id === note.id);
      noteElements.forEach(element => {
        element.remove();
      });
      this.createKonvaNoteElement(note);
    },
    // Calculates position for notes tooltip
    setNotesTooltipPosition() {
      if (!this.noteHovered) {
        return;
      }
      let pos = this.noteHovered.getAbsolutePosition(this.layers.descriptions);
      this.notesToolTipPosition = {
        x: pos.x / (1 / this.scale) + this.stage.x() + 60,
        y:
          pos.y / (1 / this.scale) +
          this.stage.y() +
          this.noteHovered.height() / 2 +
          65
      };
    },
    notesToolTipMouseEnter() {
      clearTimeout(this.noteTooltipTimeout);
      this.setNotesTooltipPosition();
      this.noteHovering = true;
    },
    notesToolTipMouseLeave() {
      clearTimeout(this.noteTooltipTimeout);
      this.noteTooltipTimeout = setTimeout(() => {
        this.noteHovering = false;
      }, 500);
    },
    showCreateWorkflowCommentModal() {
      this.showNewWFDDescriptionModal = false;
      this.$refs.WorkflowCommentModalView.note.workflow_id =
        this.workflow.id ?? "";
      this.$refs.WorkflowCommentModalView.workflowCommentModalVisible = true;
      this.$refs.WorkflowCommentModalView.type = "create";
      this.$refs.WorkflowCommentModalView.note.extra_data = {
        top: this.newWFDDescriptionPositions.top - 60,
        left: this.newWFDDescriptionPositions.left - 80,
        width: 250,
        height: 200,
        center: this.stageCenter,
        fontSize: 20
      };
    },
    editNote(note) {
      this.$refs.WorkflowCommentModalView.note = note;
      this.$refs.WorkflowCommentModalView.workflowCommentModalVisible = true;
      this.$refs.WorkflowCommentModalView.type = "update";
      this.showNewWFDDescriptionModal = false;
    },
    removeNoteFromView(note) {
      note.extra_data = null;
      FlowComments.delete(note.id, note).then(() => {
        const noteElements = this.layers.descriptions
          .getChildren()
          .filter(x => x.attrs.note.id === note.id);
        noteElements.forEach(element => {
          element.remove();
        });
        this.noteHovered = false;
        this.noteHovering = false;
      });
    },
    closeWFDContextMenu() {
      $("#wfd-description-menu").hide();
    },
    async createKonvaNoteElement(note) {
      let commentText = note.comment;
      let splitStrings = ["</p>", "</h1>", "</h2>", "</h3>", "</h4>", "</h5>"];
      splitStrings.forEach(entry => {
        commentText = commentText.split(entry).join("\n");
      });
      commentText = commentText.replace(/<[^>]+>/g, "");

      let noteText = new Konva.Text({
        note: note,
        text: commentText,
        x: note.extra_data.left,
        y: note.extra_data.top,
        width: note.extra_data.width,
        height: note.extra_data.height,
        fontSize: note.extra_data.fontSize,
        fontFamily: "Poppins, Helvetica, sans-serif",
        fill: "#555",
        padding: 10,
        align: "center",
        draggable: true,
        verticalAlign: "middle"
      });

      let textCard = new Konva.Rect({
        x: note.extra_data.left,
        y: note.extra_data.top,
        note: note,
        stroke: "#555",
        strokeWidth: 0.1,
        fill: "#fff",
        width: note.extra_data.width,
        height: note.extra_data.height,
        shadowColor: "black",
        shadowBlur: 5,
        shadowOffsetX: 1,
        shadowOffsetY: 1,
        shadowOpacity: 0.2,
        cornerRadius: 5,
        align: "center",
        draggable: true
      });

      let transformerMinWidth = 80;
      let transformerMinHeight = 80;
      let transformer = new Konva.Transformer({
        note: note,
        rotateEnabled: false,
        keepRatio: false,
        enabledAnchors: ["bottom-right"],
        anchorStroke: "red",
        anchorStrokeWidth: 1,
        borderEnabled: false,
        opacity: 0,
        boundBoxFunc: function (oldBoundBox, newBoundBox) {
          if (Math.abs(newBoundBox.width) < transformerMinWidth) {
            newBoundBox.width = transformerMinWidth;
          }
          if (Math.abs(newBoundBox.height) < transformerMinHeight) {
            newBoundBox.height = transformerMinHeight;
          }
          return newBoundBox;
        }
      });

      noteText.on("mouseover", () => {
        document.body.style.cursor = "pointer";
        this.noteHovered = noteText;
        transformer.opacity(1);
        this.notesToolTipMouseEnter();
      });
      noteText.on("mouseout", () => {
        document.body.style.cursor = "default";
        transformer.opacity(0);
        this.notesToolTipMouseLeave();
      });
      noteText.on("dragend", data => {
        const elementAttrs = data.target.attrs;
        let updateData = elementAttrs.note;
        updateData.extra_data = {
          top: elementAttrs.y,
          left: elementAttrs.x,
          width: elementAttrs.width,
          height: updateData.extra_data.height,
          center: this.stageCenter,
          fontSize: elementAttrs.fontSize
        };
        noteText.setAttrs({
          top: elementAttrs.y,
          left: elementAttrs.x
        });
        this.setNotesTooltipPosition();
        FlowComments.update(elementAttrs.note.id, updateData);
      });
      let oldWidth = noteText.width();
      noteText.on("transform", () => {
        noteText.setAttrs({
          width: Math.max(noteText.width() * noteText.scaleX(), 5),
          height: Math.max(noteText.height() * noteText.scaleY(), 5),
          scaleX: 1,
          scaleY: 1,
          verticalAlign: "middle"
        });
        const elementAttrs = noteText.attrs;
        let updateData = elementAttrs.note;
        updateData.extra_data = {
          top: elementAttrs.y,
          left: elementAttrs.x,
          width: elementAttrs.width,
          height: elementAttrs.height,
          center: this.stageCenter,
          fontSize: elementAttrs.fontSize
        };
        let newFontSize =
          noteText.attrs.note.extra_data.fontSize *
          (noteText.width() / oldWidth);
        noteText.setAttrs({
          fontSize: newFontSize
        });
        oldWidth = noteText.width();

        clearTimeout(this.noteResizeTimeout);
        this.noteResizeTimeout = setTimeout(() => {
          updateData.extra_data.fontSize = newFontSize;
          FlowComments.update(elementAttrs.note.id, updateData);
        }, 1000);
      });

      this.layers.descriptions.add(textCard);
      this.layers.descriptions.add(noteText);
      this.layers.descriptions.add(transformer);
      transformer.nodes([noteText, textCard]);

      this.centerDescriptionElements();
    },
    loadDescriptionElements() {
      const filter = [
        {
          key: "workflow_id",
          op: "equals",
          value: this.workflow.id
        }
      ];
      const params = {
        page: this.notesMeta.page,
        size: this.notesMeta.perPage
      };
      FlowComments.getAll(params, filter)
        .then(response => {
          this.notes = response.data;
          this.notes.forEach(note => {
            this.createKonvaNoteElement(note);
          });
        })
        .finally(() => {
          this.isBusy = false;
        });
      this.$nextTick().then(() => {
        this.centerDescriptionElements();
      });
    },
    centerDescriptionElements() {
      let descriptionsElements = this.layers.descriptions.getChildren();
      descriptionsElements.forEach(element => {
        const elementCenter = element.attrs.note.extra_data.center;
        const elementX = element.attrs.note.extra_data.left;
        if (elementCenter > this.stageCenter) {
          const centerDiff = elementCenter - this.stageCenter;
          element.setAttr("x", elementX - centerDiff);
        } else {
          const centerDiff = this.stageCenter - elementCenter;
          element.setAttr("x", elementX + centerDiff);
        }
      });
    },
    formatDate,
    init() {
      // Set container dimensions
      this.updateContainer();
      // Init stage
      this.stage = new Konva.Stage({
        container: "konva",
        width: this.container.width,
        height: this.container.height,
        draggable: true
      });
      // Add layers
      this.layers.main = new Konva.Layer();
      this.layers.shadows = new Konva.Layer();
      this.layers.descriptions = new Konva.Layer();
      this.stage.add(this.layers.main);
      this.stage.add(this.layers.shadows);
      this.stage.add(this.layers.descriptions);
      // Add helpers
      this.createShadows();
      // Add description elements
      this.loadDescriptionElements();

      // Init events
      this.mouseEvent();
      this.setDragInEvents();
      // Draw stage
      this.stage.batchDraw();
      let me = this;
      this.stage
        .on("click", e => {
          if (e.evt.button !== 2) {
            me.$refs.contextMenu.$el.style.display = "none";
            this.contextNode = undefined;
          }
        })
        .on("contextmenu", e => {
          if (e.evt.button !== 2) {
            return;
          }
          e.evt.preventDefault();
          if (this.contextNode !== undefined) {
            return;
          }
          if (this.showNewWFDDescriptionModal) {
            return;
          }

          const pointerPosCss = {
            top: e.evt.pageY + "px",
            left: e.evt.pageX + "px"
          };
          let wfdDescriptionMenu = $("#wfd-description-menu");
          wfdDescriptionMenu.show();
          wfdDescriptionMenu.css(pointerPosCss);
          this.newWFDDescriptionPositions = {
            top: e.evt.pageY,
            left: e.evt.pageX
          };
        })
        .on("dragmove", () => {
          me.updatePreviewRect();
          me.updateAllStates();
        });
      // Add preview
      this.createPreview();
    },
    subscribeBusEvents() {
      // Triggered when elements are loaded
      bus.$on("library-visible", this.setDragInEvents);
      // Triggered when element label changes
      bus.$on("textLabelChanged", this.labelTextChanged);
      // Toggles error sign
      bus.$on("toggleNodeError", this.updateStates);
      // Add condition to branch
      bus.$on("add-condition", this.onAddAction);
      // Remove condition from branch
      bus.$on("remove-condition", this.onRemoveCondition);
      // Move condition from branch
      bus.$on("move-condition", this.onMoveCondition);
      // select node with hash, if not selected
      bus.$on("select-node", this.onSelectNode);
      bus.$on("show-hide-critical-path", this.showHideCriticalPath);
      bus.$on("csvBatchBatchSizeChanged", this.onCsvBatchBatchSizeChanged);
      // Add new exception branch
      bus.$on("add-exception-branch", this.onAddExceptionBranch);
      // Remove exception branch
      bus.$on("remove-exception-branch", this.onRemoveExceptionBranch);
      bus.$on(
        "activate-explicit-error-handling",
        this.onActivateExplicitErrorHandling
      );
      bus.$on(
        "deactivate-explicit-error-handling",
        this.onDeactivateExplicitErrorHandling
      );
      bus.$on("refresh-branch-label", this.onRefreshBranchLabel);
      bus.$on("open-error-configuration", this.onOpenErrorConfiguration);
    },
    unsubscribeBusEvents() {
      bus.$off("library-visible", this.setDragInEvents);
      bus.$off("textLabelChanged", this.labelTextChanged);
      bus.$off("toggleNodeError", this.updateStates);
      bus.$off("add-condition", this.onAddAction);
      bus.$off("remove-condition", this.onRemoveCondition);
      bus.$off("select-node", this.onSelectNode);
      bus.$off("show-hide-critical-path", this.showHideCriticalPath);
      bus.$off("csvBatchBatchSizeChanged", this.onCsvBatchBatchSizeChanged);
      bus.$off("add-exception-branch", this.onAddExceptionBranch);
      bus.$off("remove-exception-branch", this.onRemoveExceptionBranch);
      bus.$off(
        "activate-explicit-error-handling",
        this.onActivateExplicitErrorHandling
      );
      bus.$off(
        "deactivate-explicit-error-handling",
        this.onDeactivateExplicitErrorHandling
      );
      bus.$off("refresh-branch-label", this.onRefreshBranchLabel);
      bus.$off("open-error-configuration", this.onOpenErrorConfiguration);
    },
    onAddAction(payload) {
      let branch = payload.node.getParent();
      this.addCondition(branch, payload.id);
      this.updateBranch(branch);

      branch
        .findAncestors(this.operatorGroupSelector)
        .forEach(b => this.updateSpecialGroup(b));
      branch
        .findAncestors(this.operatorGroupSelector)
        .reverse()
        .forEach(b => this.updateSpecialGroup(b));
      let operators = this.currentNode.getParent().find("Line");
      operators.forEach(operator => {
        operator.stroke(this.colors.primary);
      });
      this.stage.batchDraw();
    },
    onRemoveCondition(payload) {
      let branch = payload.node.getParent();
      this.removeCondition(branch, payload.id);
      this.updateBranch(branch);
      branch
        .findAncestors(this.operatorGroupSelector)
        .forEach(b => this.updateSpecialGroup(b));
      branch
        .findAncestors(this.operatorGroupSelector)
        .reverse()
        .forEach(b => this.updateSpecialGroup(b));
      this.stage.batchDraw();
    },
    /**
     * @param payload
     * node
     * id
     * oldIndex
     * newIndex
     */
    onMoveCondition(payload) {
      let branch = payload.node.getParent();
      const oldIndex = Number(payload.oldIndex);
      const newIndex = Number(payload.newIndex);
      let children = branch
        .getChildren()
        .filter(el => el.getClassName() === "Group");

      // Remove the element at oldIndex from the array
      const movedElement = children.splice(oldIndex, 1)[0];

      // Insert the moved element at newIndex
      children.splice(newIndex, 0, movedElement);
      if (payload.isExplicitErrorHandlingBranch ?? false) {
        this.updateBranch(branch, "error", "exceptionGroups");
      } else {
        this.updateBranch(branch);
      }

      this.stage.batchDraw();
    },
    onSelectNode(hash) {
      if (this.currentNode && this.currentNode.attrs.id === hash) {
        return;
      }

      const node = this.nodes.find(n => n.attrs.id === hash);
      if (node) {
        node.fire("click");
      }
    },
    onCsvBatchBatchSizeChanged() {
      if (!this.isBatchElement(this.currentNode)) {
        return;
      }
      this.updateLoop(this.currentNode.parent);
    },
    windowEvents() {
      // Handle window resizing and center elements afterwards
      window.addEventListener("resize", this.onResize);
    },
    onResize() {
      // const me = this;
      // clearTimeout(me.resizeTimer);
      // me.resizeTimer = setTimeout(function () {
      this.updateContainer(true);
      this.centerElements();
      this.centerDescriptionElements();
      // }, 250);
    },
    updateContainer(updateStage = false) {
      // Set dimensions of Konva container
      const panel = $(".main-panel");
      this.container = {
        width: panel[0].clientWidth - 64,
        height: panel[0].clientHeight - 100
      };
      // Update stage dimension if set
      if (updateStage && this.stage) {
        this.stage.width(this.container.width - 100);
        this.stage.height(this.container.height);
      }
    },
    // Create element shadow and add to shadow layer
    createShadows() {
      this.helperNodes.triggerShadow = new Konva.Rect({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        name: "triggerShadow",
        fill: this.colors.greenLight,
        opacity: 0.6,
        stroke: this.colors.green,
        strokeWidth: 3,
        dash: [20, 2]
      });
      this.layers.shadows.add(this.triggerShadow);
      this.triggerShadow.hide();
    },
    // Handle mouse movement
    mouseEvent() {
      let me = this;
      $(window).mousemove(function (e) {
        me.mouse.x = e.clientX;
        me.mouse.y = e.clientY;
      });
    },
    // Handle events when element gets dragged in from library
    setDragInEvents() {
      let me = this;
      let $libraryElement = $(".library .designer-element");
      let element;
      const GetConfigPromiseConstructor = () => {
        let resolve, reject;
        const promise = new Promise((res, rej) => {
          resolve = res;
          reject = rej;
        });
        return { promise, resolve, reject };
      };
      let GetConfigPromise = GetConfigPromiseConstructor();
      $libraryElement.off("drag dragend");
      $libraryElement
        .on("dragstart", e => {
          let name = e.currentTarget.dataset.name;
          element = me.library.find(el => el.name === name);

          // load element
          FlowElements.getConfig({ flowElement: element.name })
            .then(({ data }) => {
              element.config = data;
            })
            .catch(error => {
              Toast.fire({
                icon: "warning",
                title: error
              });
            })
            .finally(() => {
              GetConfigPromise.resolve();
            });
        })
        // Handle dragging event
        .on("drag", function (e) {
          element.type === "trigger"
            ? me.dragInTriggerHandler(e)
            : me.dragInHandler(e);
        })
        // Handle drop/dragend event and add node
        .on("dragend", async function (e) {
          e.preventDefault();
          await GetConfigPromise.promise;

          if (element.type === "trigger") {
            me.dragInTriggerHandler(e);
            me.triggerShadow.hide();
            await me.addElement(element);
          } else {
            let dropPosition = me.dragInHandler(e);
            if (!dropPosition) {
              me.unsetDropzone();
              GetConfigPromise = GetConfigPromiseConstructor();
              return;
            }
            let data = undefined;
            let clipboard = e.currentTarget.dataset.clipboard;
            if (clipboard !== undefined) {
              let clipboardElements = me.$store.getters[GET_CLIPBOARD_ELEMENTS];
              let element = clipboardElements[clipboard];
              data = me.getDataFromClipboard(element);
            }
            let parentNode = await me.addElement(element, dropPosition, data);
            if (data?.children?.length) {
              // sort elements in right order
              data.children.sort((a, b) => b.original_y - a.original_y);
              for (let i = 0; i < data.children.length; i++) {
                await me.setGroupDragInEvents(data.children[i], parentNode);
              }
            }
          }
          GetConfigPromise = GetConfigPromiseConstructor();
          me.unsetDropzone();
          me.stage.batchDraw();
        });
      $("#konva")
        .on("dragenter", e => {
          e.preventDefault();
        })
        .on("dragover", e => {
          e.preventDefault();
        });
    },
    async setGroupDragInEvents(data, parent) {
      let element = this.library.find(el => el.name === data.name);
      let dropzone = undefined;

      if (parent.attrs.data.flow_element_name === "loop") {
        // find loop start -- first dropzone point
        let loopStart = parent
          .getParent()
          .getChildren()
          .find(el => el.attrs.name === "loop-start");

        dropzone = loopStart
          .getChildren()
          .find(el => el.attrs.name === "dropzone");
      } else {
        // get all condition branches
        let conditions = parent
          .getParent()
          .getChildren(
            c => c.getClassName() === "Group" && !c.hasName("label-group")
          );

        // condition index = position, 0 left, 1 or if more conditions = right
        dropzone = conditions[data.conditionIndex]
          .getChildren()
          .find(el => el.attrs.name === "dropzone");
      }

      let dropPosition = {
        position: {
          x: dropzone.x(),
          y: dropzone.y()
        },
        parent: dropzone
      };
      // load element config of clipboard children
      await FlowElements.getConfig({ flowElement: element.name }).then(
        ({ data }) => {
          element.config = data;
        }
      );
      let parentNode = await this.addElement(element, dropPosition, data);
      if (data?.children?.length) {
        data.children.sort((a, b) => b.original_y - a.original_y);
        for (let i = 0; i < data.children.length; i++) {
          await this.setGroupDragInEvents(data.children[i], parentNode);
        }
      }
    },
    // Handles drag/drop events when element gets dragged in from library
    dragInHandler(e) {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
      if (!this.mouseOverCanvas) {
        this.unsetDropzone();
        return false;
      }
      let posX = this.mouseOverCanvas.x;
      let posY = this.mouseOverCanvas.y;
      let dropPosition = this.getDropPosition(posX, posY);
      if (!dropPosition) {
        this.unsetDropzone();
        return false;
      }
      this.stage.batchDraw();
      return dropPosition;
    },
    // Handles drag/drop events of trigger elements
    dragInTriggerHandler(e) {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
      if (!this.mouseOverCanvas) {
        this.triggerShadow.hide();
        return false;
      }
      this.triggerShadow.x(this.triggers.x() - 2 * this.grid.width);
      this.triggerShadow.y(this.triggers.y() - 10);
      this.triggerShadow.width(this.triggers.width() + 2 * this.grid.width);
      this.triggerShadow.height(this.triggers.height() - 5);
      this.triggerShadow.show();
      this.stage.batchDraw();
    },
    // Handles drag move events of elements in workflow
    dragMoveHandler(e) {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
      if (!this.mouseOverCanvas) {
        return false;
      }
      let posX = this.mouseOverCanvas.x;
      let posY = this.mouseOverCanvas.y;
      let dropPosition = this.getDropPosition(posX, posY, this.clone);
      if (!dropPosition) {
        return false;
      }
      this.stage.batchDraw();
      return dropPosition;
    },
    // Gets possible drop position for dragged element
    getDropPosition(x, y, clone = undefined) {
      let intersectedNode = this.stage.getIntersection({
        x: x * this.scale,
        y: y * this.scale
      });
      let result = {
        position: {
          x: undefined,
          y: undefined
        },
        following: undefined,
        parent: undefined
      };

      // Cancel if user is dragging clone but not intersecting dropzone
      if (clone && (!intersectedNode || !intersectedNode.hasName("dropzone"))) {
        this.unsetDropzone();
        return false;
      }

      let dropzone = intersectedNode;

      if (!intersectedNode?.hasName("dropzone")) {
        // If element is not dragged over dropzone
        if (clone) {
          // Cancel if user is dragging clone but not intersecting dropzone
          this.unsetDropzone();
          return false;
        } else {
          // Get last free dropzone
          let dropzones = this.layers.main.find(".dropzone");
          dropzones.sort((a, b) => b.y() - a.y());
          dropzone = dropzones[0];
          if (!dropzones || !dropzone.visible()) return false;
          let group = dropzone.getParent();
          if (group.id() === "trigger-group") {
            // Set x as middle of trigger group
            result.position.x =
              this.triggers.x() +
              this.triggers.width() / 2 -
              this.grid.width * 1.5;
            // Set y as bottom end of trigger group
            result.position.y = 100 + this.grid.height * 4;
            // Set parent as trigger group
            result.parent = this.triggers;
            let firstTrigger = this.triggers.findOne(el => el.attrs.data?.hash);
            if (firstTrigger.attrs.data.next_hash) {
              result.following = this.layers.main.findOne(
                el => el?.id() === firstTrigger.attrs.data.next_hash
              );
            }
          } else {
            let node = this.getNodeFromGroup(group);
            result.position.x =
              (node.getAbsolutePosition().x - this.stage.x()) *
              (1 / this.scale);

            let posLastElement = node.getAbsolutePosition().y - this.stage.y();
            let nodeHeight = this.getNodeHeight(node) * this.scale;

            result.position.y =
              (posLastElement + nodeHeight) * (1 / this.scale);
            result.parent = node;
          }
        }
      } else if (intersectedNode.getParent().id() === "trigger-group") {
        result = this.dropPositionTrigger(clone);
      } else if (intersectedNode.findAncestor(this.operatorGroupSelector)) {
        result = this.dropPositionGroup(intersectedNode, clone);
      } else {
        result = this.dropPositionDefault(intersectedNode, clone);
      }

      if (!result) {
        this.unsetDropzone();
        return false;
      }

      this.setDropzone(dropzone);
      return result;
    },
    checkValidDropzone(dropZoneNode, clone) {
      if (!clone || dropZoneNode.hasName("dropzone")) return true;
      return !(
        clone.id() === dropZoneNode.id() ||
        dropZoneNode.attrs.data.next_hash === clone.attrs.data.hash
      );
    },
    dropPositionTrigger(clone) {
      let result = {
        position: {}
      };
      // Set x as middle of trigger group
      result.position.x =
        this.triggers.x() + this.triggers.width() / 2 - this.grid.width * 1.5;
      // Set y as bottom end of trigger group
      result.position.y = 100 + this.grid.height * 4;
      // Set parent as trigger group
      result.parent = this.triggers;
      let firstTrigger = this.triggers.findOne(el => el.attrs.data?.hash);
      if (firstTrigger.attrs.data.next_hash) {
        result.following = this.layers.main.findOne(
          el => el?.id() === firstTrigger.attrs.data.next_hash
        );
      }
      return this.checkValidDropzone(firstTrigger, clone) ? result : false;
    },
    dropPositionGroup(intersectedNode, clone) {
      let result = {
        position: {}
      };
      if (clone) {
        // If clone is set, get original node
        let original = this.layers.main.findOne("#" + clone?.id());
        let operatorGroup = undefined;
        if (this.specialElements.includes(original?.name())) {
          // Check if the dragged element is a special element
          operatorGroup = original.getParent();
        }
        if (
          operatorGroup &&
          intersectedNode
            .findAncestors(this.operatorGroupSelector)
            .includes(operatorGroup)
        ) {
          // Return false, if an ancestor of the dropzone
          // is the currently dragged node because a
          // special element cannot be the child of itself
          return false;
        }
      }
      // Get group and node from intersected node
      let group = intersectedNode.getParent();
      let node;
      if (!this.nodeGroups.includes(group.name())) {
        node = intersectedNode;
      } else {
        node = this.getNodeFromGroup(group);
      }
      // Set x as x of intersected node
      result.position.x = node.x();
      // Set y as y of intersected node plus it's height
      result.position.y = node.hasName("dropzone")
        ? node.y()
        : node.y() + this.getNodeHeight(node);
      // Set node as parent
      result.parent = node;
      if (node.attrs.data?.next_hash) {
        result.following = this.layers.main.findOne(
          el => el?.id() === node.attrs.data.next_hash
        );
      }
      return this.checkValidDropzone(node, clone) ? result : false;
    },
    dropPositionDefault(intersectedNode, clone) {
      let result = {
        position: {}
      };
      // Get group and node from intersected node
      let group = intersectedNode.getParent();
      let node = this.getNodeFromGroup(group);
      // Set x as x of intersected node
      result.position.x =
        (node.getAbsolutePosition().x - this.stage.x()) * (1 / this.scale);
      // Set y as y of intersected node plus it's height
      result.position.y =
        (node.getAbsolutePosition().y +
          this.getNodeHeight(node) -
          this.stage.y()) *
        (1 / this.scale);
      // Set node as parent
      result.parent = node;
      // If node has next_hash, set it as following
      if (node.attrs.data.next_hash) {
        result.following = this.layers.main.findOne(
          el => el?.id() === node.attrs.data.next_hash
        );
      }
      return this.checkValidDropzone(node, clone) ? result : false;
    },
    setDropzone(dropzone) {
      if (this.lastDropzone === dropzone) {
        return;
      }

      if (this.lastDropzone) {
        this.unsetDropzone();
      }

      dropzone.stroke(this.colors.primary);
      dropzone.shadowEnabled(true);
      dropzone.draw();

      this.lastDropzone = dropzone;
    },
    unsetDropzone() {
      if (!this.lastDropzone) return;
      this.lastDropzone.stroke(this.colors.black);
      this.lastDropzone.shadowEnabled(false);
      this.lastDropzone.draw();
      this.lastDropzone = undefined;
    },
    async addElement(
      element,
      dropPosition = { position: { x: 0, y: 0 } },
      data = undefined
    ) {
      let imgSrc = data?.icon
        ? data.icon
        : element.icon
          ? element.icon
          : process.env.BASE_URL + "media/logos/xentral-icon.png";
      let img = await this.prepareImage(imgSrc);
      let node = this.createShape(element, dropPosition.position, img, data);
      let textLabel = this.createTextLabel(
        node,
        resolveLocalizedString(node.attrs.data.planning?.label)
      );
      let textType = this.createTextType(
        node,
        resolveLocalizedString(element.label)
      );
      this.createStates(node);
      let group = new Konva.Group({ name: "node-group" });
      group.add(node, textLabel, textType);

      if (element.type === "trigger") {
        await this.addTrigger(group);
        return;
      }

      switch (element.name) {
        case "branch":
        case "loop":
        case "cache":
          await this.addSpecialNode(group, data, element);
          break;
        default:
          if (this.isBatchElement(node)) {
            await this.addSpecialNode(group, data, element, true);
          } else if (this.hasExplicitErrorHandling(node)) {
            await this.addSpecialNode(group, data, element, false, true);
          } else {
            await this.addNode(group);
          }

          break;
      }

      if (node.attrs.data.group_hash) {
        let targetGroup = this.layers.main.findOne(
          el =>
            el.getClassName() === "Group" &&
            el.id().includes(node.attrs.data.group_hash) &&
            !el.hasName("loop-group")
        );
        this.moveFromNewToGroup(
          node,
          undefined,
          this.getDropzoneFromGroup(targetGroup),
          dropPosition.position
        );
      } else {
        this.moveNode(
          node,
          dropPosition.following,
          dropPosition.parent,
          dropPosition.position
        );
      }
      this.loadInactiveNodes(node);

      return node;
    },
    // Async method to prepare img object
    async prepareImage(src) {
      return new Promise(resolve => {
        let img = new Image();
        img.onload = () => resolve(img);
        //We are not allowed to cross origin -> img.crossOrigin = 'Anonymous';
        img.src = src;
        img.onerror = function () {
          img.src = process.env.BASE_URL + "media/logos/xentral-icon.png";
        };
      });
    },
    // Adds node to workflow
    async addNode(group) {
      let node = this.getNodeFromGroup(group);
      // Set group id
      group.id("group-" + node.getAttr("data").hash);
      // let newGroup = new Konva.Group({ name: "operator-group" });
      // Create operator
      let operator = this.createOperator(node);
      if (node.getAttr("data").is_endpoint) operator.hide();
      group.add(operator);
      let dropzone = await this.createDropZone();
      group.add(dropzone);
      this.updateDropzone(node);
      // group.add(newGroup)
      // Return group
      return group;
    },
    async addTrigger(group) {
      let node = this.getNodeFromGroup(group);
      // Allow multiple event triggers
      if (
        node.attrs.name !== "event" &&
        this.layers.main.find("." + node.name()).length > 0
      ) {
        this.$toast.fire({
          icon: "warning",
          title:
            'Trigger des Typs: "' + node.attrs.label + '" existiert bereits'
        });
        return;
      }
      // Create operator
      let operator = this.createOperator(node);
      group.add(operator);
      // Add trigger to trigger group
      this.triggers.add(group);
      this.stage.batchDraw();
      // Redraw triggers
      this.updateTriggers();
      this.updatePreview();
      // Return group
      return group;
    },
    addSpecialNode(
      group,
      data,
      element,
      isBatch = false,
      hasExplicitErrorHandling = false
    ) {
      switch (element.name) {
        case "branch":
        case "cache":
          this.addBranch(group, data);
          break;
        case "loop":
          this.addLoop(group, data);
          break;
      }
      if (isBatch) {
        this.addLoop(group, data);
      }
      if (hasExplicitErrorHandling) {
        this.addExceptionBranch(group);
      }
    },
    async addBranch(group) {
      let node = this.getNodeFromGroup(group);
      let configuration = node.attrs.data.configuration;
      let conditionsConfig = configuration.find(el => el.name === "conditions");
      // Set branch arms count
      let conditions = conditionsConfig?.value?.length
        ? conditionsConfig.value
        : this.getDefaultConditions(node.attrs.name);
      conditionsConfig.value = conditions;
      // Set group config
      group.id("group-" + node.getAttr("data").hash);
      group.name("branch-group");
      let dropzone = this.createDropZone();
      group.add(dropzone);

      // Create branch arms and init their components
      conditions.forEach(condition => {
        this.addCondition(group, condition.id);
      });
      this.updateBranch(group);
      if (node.getAttr("data").is_endpoint) {
        let connectorLines = this.getConnectorLineFromNode(
          group,
          group.attrs.id
        );
        dropzone.visible(!node.attrs.data.is_endpoint);
        connectorLines.forEach(connectorLine => {
          connectorLine.visible(!node.attrs.data.is_endpoint);
        });
      }
      return group;
    },
    addCondition(branchGroup, id, addDropzone = true) {
      let labelGroup = new Konva.Group({ name: "label-group" });
      labelGroup.add(
        new Konva.Shape({
          name: "label-circle",
          width: 15,
          height: 15,
          stroke: this.colors.black,
          strokeWidth: 1,
          sceneFunc: circleScene
        }),
        new Konva.Text({
          name: "label-text",
          text: "1",
          fontSize: 11,
          lineHeight: 1,
          fontFamily: "Poppins, Helvetica, sans-serif",
          fill: this.colors.black,
          align: "center",
          width: 10,
          height: 10,
          x: 2,
          y: 3
        }),
        new Konva.Text({
          name: "label-description",
          text: "",
          fontSize: 11,
          lineHeight: 1,
          fontFamily: "Poppins, Helvetica, sans-serif",
          fill: this.colors.black,
          align: "left",
          height: 10,
          width: 100,
          x: 20,
          y: 3
        })
      );
      let conditionGroup = new Konva.Group({
        id: "condition-" + id,
        name: "condition"
      });
      conditionGroup.add(
        new Konva.Line({
          name: "top",
          stroke: this.colors.black,
          strokeWidth: 1,
          lineCap: "round",
          lineJoin: "round"
        }),
        new Konva.Line({
          name: "bottom",
          stroke: this.colors.black,
          strokeWidth: 1,
          lineCap: "round",
          lineJoin: "round"
        }),
        new Konva.Line({
          name: "connectorLine",
          stroke: this.colors.black,
          strokeWidth: 1,
          lineCap: "round",
          lineJoin: "round"
        })
      );

      if (addDropzone) {
        let dropzone = this.createDropZone();
        conditionGroup.add(dropzone);
      }
      conditionGroup.add(labelGroup);
      branchGroup.add(conditionGroup);
    },
    removeCondition(branchGroup, id) {
      let condition = branchGroup.findOne("#condition-" + id);
      condition
        .find(
          el =>
            el.getClassName() === "Shape" &&
            !["dropzone", "label-circle"].includes(el.name())
        )
        .forEach(node => this.deleteNode(node));
      condition.destroy();
    },
    updateBranch(group, dataKey = "configuration", elName = "conditions") {
      // Get node from branch
      let node = this.getNodeFromGroup(group);
      // Conditions from node data
      let dataConditions = node.attrs.data[dataKey].find(
        el => el.name === elName
      ).value;
      let dataCondition = undefined;
      let index = 0;
      let conditionId = "";
      // Get initial branch height for delta calculating
      let initialHeight = this.getNodeHeight(node);
      // Get condition groups and sort ascending
      let conditionGroups = group
        .getChildren()
        .filter(child => child.getClassName() === "Group")
        .sort(function (a, b) {
          return a.name() - b.name();
        });
      // Set a base condition width
      let baseConditionWidth = this.grid.width * 3;
      // Get largest width of conditions
      let conditionWidth = baseConditionWidth;
      conditionGroups.forEach(condition => {
        // Collect operators from inside condition
        let operatorGroups = condition.getChildren(child => {
          return (
            child.getClassName() === "Group" && this.isOperatorGroup(child)
          );
        });
        // Set width of branch as new condition width if greater
        operatorGroups.forEach(operatorGroup => {
          // Update branch
          this.updateSpecialGroup(operatorGroup);
          let operatorNode = this.getNodeFromGroup(operatorGroup);
          this.nodeMoved(operatorNode);
          // Get width of branch
          let operatorNodeWidth = this.getNodeWidth(operatorNode);
          // Set new condition width if branch is too big
          if (operatorNodeWidth > conditionWidth - 2 * this.grid.width) {
            conditionWidth = operatorNodeWidth + 2 * this.grid.width;
          }
        });
      });
      // Get count of conditions
      let conditionsCount = Object.keys(conditionGroups).length;
      // Get total width of origin branch
      let totalWidth = conditionWidth * (conditionsCount - 1);
      // Get heights of each condition and maximum height for botton line
      let conditionHeights = {};
      let maxConditionHeight = 0;
      // Check if all conditions are set as endpoints
      let allConditionsEndPoint = true;
      // Render nodes in condition
      conditionGroups.forEach(condition => {
        // Get condition from node's data
        let conditionId = condition.id().split("-")[1];
        let dataCondition = dataConditions.find(el => el.id === conditionId);
        index = dataConditions.indexOf(dataCondition);
        let me = this;
        let x = conditionWidth * index - this.grid.width / 2;
        let y = this.grid.height * 3;
        // Order nodes in condition by y value
        let groups = condition
          .getChildren(
            c => c.getClassName() === "Group" && !c.hasName("label-group")
          )
          .sort(function (a, b) {
            return me.getNodeFromGroup(a)?.y() - me.getNodeFromGroup(b)?.y();
          });
        // Set inital height of condition to 0
        conditionHeights[index] = 0;
        // Show bottom operator initially
        let showBottomOperator = true;
        // If condition has no elements
        if (!groups.length) {
          // Set branch no endpoint
          allConditionsEndPoint = false;
          // Set condition's next_hash to undefined

          if (dataCondition) {
            dataCondition.next_hash = undefined;
          }
        }
        // Set position of nodes in condition
        groups.forEach((group, ii) => {
          let childNode = this.getNodeFromGroup(group);
          // Set condition's next_hash to first component's id
          if (ii === 0 && dataCondition) {
            dataCondition.next_hash = childNode.id();
          }
          // Set node to position value
          childNode.position({
            x: x,
            y: y
          });
          // Set node's next_hash to next node if exists
          childNode.attrs.data.next_hash = groups[ii + 1]
            ? this.getNodeFromGroup(groups[ii + 1])?.id()
            : "";
          // Get node height
          let nodeHeight = this.getNodeHeight(childNode);
          // Add node height to y value
          y += nodeHeight;
          // Add node height to conditions total height
          conditionHeights[index] += nodeHeight;
          let dropzone = this.getDropzoneFromGroup(group);
          if (dropzone) {
            dropzone.y(nodeHeight);
            if (ii === groups.length - 1) {
              dropzone.y(dropzone.y() - 7.5);
            }
          }
          // Actually move node
          this.nodeMoved(childNode);
          // If last node is end point hide the bottom operator
          if (childNode.attrs.data.is_endpoint) showBottomOperator = false;
          // Else branch node is available for next_hash
          else allConditionsEndPoint = false;
        });
        // If condition's height is bigger than current maximum, replace it
        if (conditionHeights[index] > maxConditionHeight)
          maxConditionHeight = conditionHeights[index];
        // Show bottom operator depending on end point presence

        if (
          elName === "exceptionGroups" &&
          !this.isExplicitErrorHandlingActive(node)
        )
          showBottomOperator = false;

        condition.findOne(".bottom").visible(showBottomOperator);
      });
      // If all conditions have an end point, set nodes end point to true
      if (!node.attrs.data.is_endpoint) {
        node.attrs.data.is_endpoint = allConditionsEndPoint;
      }
      conditionGroups.forEach(condition => {
        conditionId = condition.id().split("-")[1];
        dataCondition = dataConditions.find(el => el.id === conditionId);
        index = dataConditions.indexOf(dataCondition);
        // Update condition group dimensions
        condition.width(conditionWidth);
        if (maxConditionHeight > 0) {
          condition.height(this.grid.height * 4 + maxConditionHeight);
        } else {
          condition.height(this.grid.height * 7 + maxConditionHeight);
        }
        // Update condition group positions
        condition.x(node.x() - (totalWidth / 2 - this.grid.width / 2));
        condition.y(node.y() + node.height());
        // Get operators
        let operatorTop = condition.findOne(".top");
        let operatorBottom = condition.findOne(".bottom");
        let connectorLine = condition.findOne(".connectorLine");

        let y4 =
          elName === "exceptionGroups" &&
          !this.isExplicitErrorHandlingActive(node)
            ? this.grid.height * 2
            : this.grid.height * 3;

        // Update operator top points
        operatorTop.points([
          totalWidth / 2,
          0,
          totalWidth / 2,
          this.grid.height,
          conditionWidth * index,
          this.grid.height,
          conditionWidth * index,
          y4
        ]);
        // Update operator bottom points
        if (maxConditionHeight > 0) {
          operatorBottom.points([
            conditionWidth * index,
            this.grid.height * 3 + conditionHeights[index],
            conditionWidth * index,
            this.grid.height * 3 + maxConditionHeight,
            totalWidth / 2,
            this.grid.height * 3 + maxConditionHeight
          ]);
          connectorLine.points([
            totalWidth / 2,
            this.grid.height * 3 + maxConditionHeight,
            totalWidth / 2,
            this.grid.height * 5 + maxConditionHeight
          ]);
        } else {
          operatorBottom.points([
            conditionWidth * index,
            this.grid.height * 3 + conditionHeights[index],
            conditionWidth * index,
            this.grid.height * 5 + maxConditionHeight,
            totalWidth / 2,
            this.grid.height * 5 + maxConditionHeight
          ]);
          connectorLine.points([
            totalWidth / 2,
            this.grid.height * 5 + maxConditionHeight,
            totalWidth / 2,
            this.grid.height * 7 + maxConditionHeight
          ]);
        }
        if (maxConditionHeight > 0) {
          condition.height(this.grid.height * 5 + maxConditionHeight);
        } else {
          condition.height(this.grid.height * 7 + maxConditionHeight);
        }
        // Get dropzone
        let dropzone = condition.findOne(".dropzone");
        if (dropzone) {
          // Position dropzone
          dropzone.x(conditionWidth * index - 7.5);
          if (conditionHeights[index] > 0) {
            dropzone.y(this.grid.height * 2 - 7.5);
          } else if (maxConditionHeight > 0) {
            dropzone.y(this.grid.height * 4 - 7.5);
          } else {
            dropzone.y(this.grid.height * 3 - 7.5);
          }
        }

        // Set label circles
        let labelGroup = condition
          .getChildren()
          .find(c => c.hasName("label-group"));
        labelGroup.position({
          x: conditionWidth * index - 7.5,
          y: this.grid.height - 18
        });
        let labelText = labelGroup.findOne(".label-text");
        let label = index + 1;
        if (
          dataCondition &&
          (dataCondition.value === true || dataCondition.first)
        ) {
          label = "✓";
        }
        if (index >= dataConditions.length - 1) {
          label = "×";
          labelText.fontSize(14);
          labelText.y(1.5);
        }
        labelText.text(label);

        let labelDescription = labelGroup.findOne(".label-description");

        if (labelDescription && dataCondition && dataCondition.label) {
          labelDescription.text(dataCondition.label);
        }
      });
      this.updateDropzone(node);
      if (group.getParent() && !this.isLoadingCanvas) {
        // Move elements below by delta of current node height and initial height defined at the beginning
        this.moveNodesBelow(
          node,
          group.getParent(),
          this.getNodeHeight(node) - initialHeight
        );
      }
      return group;
    },
    addLoop(group) {
      let node = this.getNodeFromGroup(group);
      // Set group config
      group.id("group-" + node.getAttr("data").hash);
      group.name("loop-group");
      // Create group for loop nodes
      let groupStart = new Konva.Group({
        id: "loop-" + node.getAttr("data").hash,
        name: "loop-start"
      });
      // Create group for loop back
      let groupBack = new Konva.Group({ name: "loop-back" });
      // Add to group
      group.add(groupStart, groupBack);
      group.add(this.createDropZone());

      // update csvBatch
      if (this.isBatchElement(node)) {
        groupStart.add(
          new Konva.Text({
            x: node.width() * 2 - 10,
            y: 10,
            name: "batchSize",
            text: "à 0",
            fontSize: 12,
            fontStyle: "italic",
            lineHeight: 1.2,
            fontFamily: "Poppins, Helvetica, sans-serif",
            fill: "#B5B5C3",
            width: this.grid.width * 2 - 10
          })
        );
      }

      groupStart.add(
        // top right
        new Konva.Line({
          name: "top",
          stroke: this.colors.black,
          strokeWidth: 1,
          lineCap: "round",
          lineJoin: "round"
        }),
        // bottom right
        new Konva.Line({
          name: "bottom",
          stroke: this.colors.black,
          strokeWidth: 1,
          lineCap: "round",
          lineJoin: "round"
        })
      );
      groupStart.add(this.createDropZone());
      groupBack.add(
        new Konva.Arrow({
          name: "arrow",
          fill: this.colors.black,
          stroke: this.colors.black,
          strokeWidth: 1,
          lineCap: "round",
          lineJoin: "round"
        })
      );
      groupBack.add(
        // bottom
        new Konva.Line({
          name: "connectorLine",
          stroke: this.colors.black,
          strokeWidth: 1,
          lineCap: "round",
          lineJoin: "round"
        })
      );
      this.updateLoop(group);
      // if loop is endpoint, hide connectorLine
      if (node.getAttr("data").is_endpoint) {
        let connectorLines = this.getConnectorLineFromNode(
          group,
          group.attrs.id
        );
        connectorLines.forEach(connectorLine => {
          connectorLine.visible(!node.attrs.data.is_endpoint);
        });
      }
      return group;
    },
    updateLoop(loopGroup) {
      let me = this;
      // Get node from loop
      let node = this.getNodeFromGroup(loopGroup);
      // Get initial branch height for delta calculating
      let initialHeight = this.getNodeHeight(node);
      // Get both loop arms
      let loopStart = loopGroup.findOne(".loop-start");
      let loopBack = loopGroup.getChildren(c => c.hasName("loop-back"))[0];
      // Get nodes in loop
      let loopNodes = loopStart
        .getChildren(c => c.getClassName() === "Group")
        .sort((a, b) => {
          return me.getNodeFromGroup(a)?.y() - me.getNodeFromGroup(b)?.y();
        });
      // Set loop variables
      let totalWidth = 3 * this.grid.width;
      let startHeight = 3 * this.grid.height;
      let maxHeight = 0;
      // Get actual group width by child nodes
      loopNodes.forEach(loopNodeGroup => {
        let loopNode = this.getNodeFromGroup(loopNodeGroup);
        if (
          !this.specialElements.includes(loopNode.name()) &&
          !this.hasExplicitErrorHandling(loopNode)
        )
          return;
        this.updateSpecialGroup(loopNodeGroup);
        let operatorWidth = this.getNodeWidth(loopNode);
        if (operatorWidth > totalWidth - 2 * this.grid.width)
          totalWidth = operatorWidth + 2 * this.grid.width;
      });
      // Set variables for render
      let x = totalWidth - this.grid.width / 2;
      let y = startHeight;
      if (!loopNodes.length) {
        node.attrs.data.next_inner_hash = "";
      }
      // Render nodes
      loopNodes.forEach((loopNodeGroup, i) => {
        let loopNode = this.getNodeFromGroup(loopNodeGroup);
        // Set loop's next_hash
        if (i === 0) {
          node.attrs.data.next_inner_hash = loopNode.id();
        }
        // Set node to position value
        loopNode.position({
          x: x,
          y: y
        });
        // Set node's next_hash to next node if exists
        loopNode.attrs.data.next_hash = loopNodes[i + 1]
          ? this.getNodeFromGroup(loopNodes[i + 1])?.id()
          : "";
        // Get node height
        let nodeHeight = this.getNodeHeight(loopNode);
        // Add node height to y value
        y += nodeHeight;
        // Add node height to conditions total height
        maxHeight += nodeHeight;
        // Update dropzone of node
        let dropzone = this.getDropzoneFromGroup(loopNodeGroup);
        if (dropzone) {
          dropzone.y(nodeHeight);
          if (i === loopNodes.length - 1) {
            dropzone.y(dropzone.y() - 7.5);
          }
        }
        // Actually move node
        this.nodeMoved(loopNode);
      });
      // Update loop start group dimensions
      loopStart.width(totalWidth);
      if (maxHeight > 0) {
        loopStart.height(this.grid.height * 5 + maxHeight);
      } else {
        loopStart.height(this.grid.height * 7 + maxHeight);
      }
      // Update condition group positions
      loopStart.x(node.x() - (totalWidth / 2 - this.grid.width / 2));
      loopStart.y(node.y() + node.height());
      // Get operators
      let operatorTop = loopStart.findOne(".top");
      let operatorBottom = loopStart.findOne(".bottom"); // Get operators
      let arrow = loopBack.findOne("Arrow");
      let connectorLine = loopBack.findOne(".connectorLine");

      this.updateArrowOpacity(arrow, node);
      this.updateBatchSize(loopStart, node);

      // Update operator top points
      operatorTop.points([
        totalWidth / 2,
        0,
        totalWidth / 2,
        this.grid.height,
        totalWidth,
        this.grid.height,
        totalWidth,
        this.grid.height * 3
      ]);
      if (loopNodes.length > 0) {
        operatorBottom.points([
          totalWidth,
          this.grid.height * 3 + maxHeight,
          totalWidth,
          this.grid.height * 3 + maxHeight,
          totalWidth / 2,
          this.grid.height * 3 + maxHeight
        ]);
        // Set arrow points
        arrow.points([
          totalWidth / 2,
          this.grid.height * 3 + maxHeight,
          0,
          this.grid.height * 3 + maxHeight,
          0,
          this.grid.height,
          totalWidth / 2 - 10,
          this.grid.height
        ]);
        connectorLine.points([
          totalWidth / 2,
          this.grid.height * 5 + maxHeight,
          totalWidth / 2,
          this.grid.height * 3 + maxHeight
        ]);
      } else {
        operatorBottom.points([
          totalWidth,
          this.grid.height * 3 + maxHeight,
          totalWidth,
          this.grid.height * 5 + maxHeight,
          totalWidth / 2,
          this.grid.height * 5 + maxHeight
        ]);
        // Set arrow points
        arrow.points([
          totalWidth / 2,
          this.grid.height * 5 + maxHeight,
          0,
          this.grid.height * 5 + maxHeight,
          0,
          this.grid.height,
          totalWidth / 2 - 10,
          this.grid.height
        ]);
        connectorLine.points([
          totalWidth / 2,
          this.grid.height * 7 + maxHeight,
          totalWidth / 2,
          this.grid.height * 5 + maxHeight
        ]);
      }
      // Get dropzone
      let dropzone = loopStart.findOne(".dropzone");
      // Position dropzone
      dropzone.x(totalWidth - 7.5);
      if (loopNodes.length > 0) {
        dropzone.y(this.grid.height * 2 - 7.5);
      } else {
        dropzone.y(this.grid.height * 3 - 7.5);
      }
      // Update loop back group dimensions
      loopBack.width(totalWidth);
      loopBack.height(this.grid.height * 4 + maxHeight);
      // Update condition group positions
      loopBack.x(node.x() - (totalWidth / 2 - this.grid.width / 2));
      loopBack.y(node.y() + node.height());
      this.updateDropzone(node);
      if (loopGroup.getParent() && !this.isLoadingCanvas) {
        // Move elements below by delta of current node height and initial height defined at the beginning
        this.moveNodesBelow(
          node,
          loopGroup.getParent(),
          this.getNodeHeight(node) - initialHeight
        );
      }
      return loopGroup;
    },
    updateArrowOpacity(arrow, node) {
      // update csvBatch
      if (!this.isBatchElement(node)) {
        return;
      }
      let opacity = 1.0;
      const fieldBatchSize = node.attrs.data.configuration.find(
        f => f.name === "batchSize"
      );
      const fieldLimit = node.attrs.data.configuration.find(
        f => f.name === "limit"
      );

      const batchSize =
        fieldBatchSize &&
        fieldBatchSize.value !== undefined &&
        fieldBatchSize.value !== null &&
        fieldBatchSize.value !== 0 &&
        fieldBatchSize.value !== "0" &&
        fieldBatchSize.value !== "" &&
        !isNaN(fieldBatchSize.value)
          ? Number(fieldBatchSize.value)
          : 0;

      const limit =
        fieldLimit &&
        fieldLimit.value !== undefined &&
        fieldLimit.value !== null &&
        fieldLimit.value !== 0 &&
        fieldLimit.value !== "0" &&
        fieldLimit.value !== "" &&
        !isNaN(fieldLimit.value)
          ? Number(fieldLimit.value)
          : 0;

      if (batchSize === 0 || (limit !== 0 && limit <= batchSize)) {
        opacity = 0.3;
      }
      arrow.opacity(opacity);
    },
    updateBatchSize(loopStart, node) {
      // update csvBatch
      if (!this.isBatchElement(node)) {
        return;
      }
      const textField = loopStart.findOne(".batchSize");
      if (!textField) {
        return;
      }
      textField.x(loopStart.width() / 2 + 15);
      const fieldBatchSize = node.attrs.data.configuration.find(
        f => f.name === "batchSize"
      );

      if (
        !fieldBatchSize ||
        fieldBatchSize.value === undefined ||
        fieldBatchSize.value === null ||
        isNaN(fieldBatchSize.value) ||
        fieldBatchSize.value.toString().trim().length === 0 ||
        Number(fieldBatchSize.value).toString().trim() === "0"
      ) {
        textField.hide();
        return;
      }
      textField.show();
      const batchSize =
        fieldBatchSize.value.toString().length > 0 ? fieldBatchSize.value : "0";
      textField.text("à " + batchSize);
    },
    createOperatorPlaceholder() {
      return new Konva.Shape({
        x: 0,
        y: 0,
        width: this.grid.width,
        height: this.grid.height * 2,
        name: "placeholder",
        stroke: this.colors.black,
        strokeWidth: 1,
        dash: [20, 2],
        fill: this.colors.white,
        sceneFunc: circleScene,
        draggable: false
      });
    },
    // Creates new shape by library element
    createShape(element, position, img, data = null) {
      let me = this;
      let hash = generateHash(20, "we");
      if (data && data.hash) {
        hash = data.hash;
      }

      let nextHash = "";
      let planning = {
        label: "",
        description: "",
        comment: "",
        icon: ""
      };

      let elementCopy = JSON.parse(JSON.stringify(element));
      let save = true;
      let active = true;

      if (data) {
        active = data.active;
        save = data.save ?? true;
        nextHash = data.next_hash ?? "";
        planning.label = data.label ?? "";
        planning.description = data.description ?? "";
        planning.comment = data.comment ?? "";
        planning.icon = data.icon ?? "";

        if (!data.config) {
          data.config = {
            authentication: [],
            configuration: [],
            input: [],
            output: [],
            error: []
          };
        }

        if (
          data.configFields !== undefined &&
          data.configFields.authentication !== undefined
        ) {
          elementCopy.config = JSON.parse(JSON.stringify(data.configFields));
        }

        if (!elementCopy.config) {
          elementCopy.config = data.config;
        }

        this.setData(
          data,
          data.config.authentication,
          elementCopy.config.authentication
        );
        this.setData(
          data,
          data.config.configuration,
          elementCopy.config.configuration
        );
        this.setData(data, data.config.input, elementCopy.config.input);
        if (data.flow_element_name === "assignment") {
          if (data.config.input.variables) {
            bus.$emit("update-custom-variable-value", {
              id: hash,
              field: { value: data.config.input.variables },
              prefix: "output"
            });
          }
        }
        this.setData(
          data,
          data.config.output,
          elementCopy.config.output,
          false
        );
        this.setData(data, data.config.error, elementCopy.config.error);
        for (const error in data.config.error) {
          if (error !== "exceptionMessage") continue;
          let field = elementCopy.config.error.find(f => f.name === error);
          let index = elementCopy.config.error.map(f => f.name).indexOf(error);
          bus.$emit("update-custom-variable-value", {
            id: hash + "-" + index,
            field: field,
            prefix: "error"
          });
          if (!field || !field.hash) {
            continue;
          }
          field.hash = hash;
        }
        for (const output in data.config.output) {
          const d = data.config.output[output];
          let index = elementCopy.config?.output
            ? elementCopy.config.output.map(f => f.name).indexOf(output)
            : -1;

          bus.$emit("update-custom-variable-value", {
            id: hash + "-" + index,
            field: d,
            prefix: "output"
          });
          if (!d || !d.hash) {
            continue;
          }
          d.hash = hash;
        }

        if (elementCopy.name === "branch") {
          elementCopy.config.configuration.conditions =
            data.config.configuration.conditions;
        }
      } else {
        this.setDefaultValue(elementCopy.config.authentication);
        this.setDefaultValue(elementCopy.config.configuration);
        this.setDefaultValue(elementCopy.config.input);
        this.setDefaultValue(elementCopy.config.output);
        this.setDefaultValue(elementCopy.config.error);
      }

      // defines the margin between shape border and icon
      let offset = 0.2;
      let useWidth = !(img.width < img.height);
      let gridWidth = this.grid.width;
      let gridHeight = this.grid.height;
      let ratio = useWidth
        ? img.width / gridWidth
        : img.height / (gridHeight * 2);
      let scaleFactor = (1 / ratio) * (1 - offset * 2);
      let translateX = useWidth
        ? gridWidth * offset
        : (gridWidth - img.width * scaleFactor) / 2;
      let translateY = !useWidth
        ? gridHeight * 2 * offset
        : (gridHeight * 2 - img.height * scaleFactor) / 2;

      let shape = "";
      shape = new Konva.Shape({
        opacity: 1,
        radius: gridWidth / 2,
        fillPatternImage: img,
        id: hash,
        x: position.x,
        y: position.y,
        width: gridWidth,
        height: gridHeight * 2,
        name: element.name,
        label: element.label,
        type: element.type,
        stroke: this.colors.black,
        strokeWidth: 1,
        fillPatternRepeat: "no-repeat",
        fillPatternScale: {
          x: scaleFactor,
          y: scaleFactor
        },
        fillPatternX: translateX,
        fillPatternY: translateY,
        fillPriority: "pattern",
        sceneFunc: circleScene,
        draggable: element.type !== "trigger",
        dragBoundFunc: function (pos) {
          let height = this.height();
          let newY = pos.y <= 0 ? 0 : pos.y;
          newY =
            newY + height >= me.stageHeight ? me.stageHeight - height : newY;
          return {
            x: pos.x,
            y: newY
          };
        },
        shadowColor: this.colors.black,
        shadowBlur: 10,
        shadowOffset: { x: 0, y: 0 },
        shadowOpacity: 0.7,
        shadowEnabled: false
      });

      // Sets necessary data information
      // TODO: Check what for is code used
      if (shape.name() === "php") {
        let code = elementCopy.config.configuration?.find(
          el => el.name === "code"
        );
        if (code && !code?.value) {
          code.value =
            "<?php\n" +
            "// Get output value: $process->getOutputValue('foo');\n" +
            "// Set output value: $process->setOutputValue('foo', 'bar');\n" +
            "// Log: $this->logger->log('Test');\n";
        }
      }

      shape.setAttr("data", {
        active: active,
        save: save,
        authentication: elementCopy.config.authentication,
        configuration: elementCopy.config.configuration,
        error: elementCopy.config.error,
        input: elementCopy.config.input,
        output: elementCopy.config.output,
        planning: planning,
        hash: hash,
        next_hash: nextHash,
        group_hash: data?.group_hash ?? "",
        workflow_element_id: elementCopy.id,
        flow_element_name: elementCopy.name ?? null,
        workflow_id: this.workflow.id,
        is_endpoint: data?.is_endpoint ?? false,
        flow_element_exists: element.flow_element_exists
      });
      if (data?.x) shape.x(data.x);
      if (data?.y) shape.y(data.y);
      if (shape.attrs.data.hash && shape.attrs.data.hash !== shape.id())
        shape.id(shape.attrs.data.hash);
      // Sets node events
      this.setShapeEvents(shape);
      this.nodes.push(shape);
      return shape;
    },
    setDefaultValue(area) {
      const fields = Object.values(
        area?.filter(f => f.default !== undefined) ?? {}
      );
      fields.forEach(field => {
        if (field.type === "multiSelect") {
          this.$set(field, "value", [field.default]);
          return;
        }
        this.$set(field, "value", field.default);
      });
    },
    setData(data, areaData, area, onlyValue = true) {
      if (
        typeof area === "undefined" ||
        area.length === 0 ||
        !Array.isArray(area)
      ) {
        return false;
      }
      for (const key in areaData) {
        const areaFields = area.filter(e => e.name === key);

        areaFields.forEach(el => {
          let setData = true;
          if (el.dependsOn) {
            el.dependsOn.forEach(dependOn => {
              const dependOnArea = dependOn.name.split(".").shift();
              const dependOnField = dependOn.name.split(".").pop();

              if ("notValues" in dependOn) {
                let setDataTemp = true;
                dependOn.notValues.forEach(dependsOnValue => {
                  if (
                    dependsOnValue === data.config[dependOnArea][dependOnField]
                  ) {
                    setDataTemp = false;
                  }
                });
                setData = setDataTemp;
              } else {
                let setDataTemp = false;
                dependOn.values.forEach(dependsOnValue => {
                  if (
                    dependsOnValue === data.config[dependOnArea][dependOnField]
                  ) {
                    setDataTemp = true;
                  }
                });
                setData = setDataTemp;
              }
            });
          }
          if (setData) {
            if (el.type === "select" && el.options === undefined) {
              el.options = [
                {
                  value: areaData[key],
                  label: areaData[key]
                }
              ];
            } else if (el.type === "multiSelect" && el.options === undefined) {
              el.options = [];
              Object.values(areaData[key]).forEach(o => {
                el.options.push({
                  value: o,
                  label: o
                });
              });
            }

            const obj = onlyValue ? el : area;
            const index = onlyValue ? "value" : area.indexOf(el);
            this.$set(obj, index, areaData[key]);
          }
        });
      }
    },
    createDropZone() {
      let dropzone = new Konva.Shape({
        width: 15,
        height: 15,
        x: 0,
        y: 0,
        name: "dropzone",
        stroke: this.colors.black,
        strokeWidth: 1,
        hitStrokeWidth: 10,
        sceneFunc: dropzoneScene,
        hitFunc: function (context, shape) {
          let width = shape.width();
          let height = shape.height();
          let bufferX = 30;
          let bufferY = 10;
          context.beginPath();
          context.moveTo(-bufferX, -bufferY);
          context.lineTo(width + bufferX, -bufferY);
          context.lineTo(width + bufferX, height + bufferY);
          context.lineTo(-bufferX, height + bufferY);
          context.closePath();
          context.fillStrokeShape(this);
        },
        shadowColor: this.colors.black,
        shadowBlur: 10,
        shadowOffset: { x: 0, y: 0 },
        shadowOpacity: 0.7,
        shadowEnabled: false
      });
      return dropzone;
    },
    // Creates operator for node
    createOperator(node) {
      if (
        !this.specialElements.includes(node.name()) &&
        !this.hasExplicitErrorHandling(node)
      ) {
        return new Konva.Line({
          points: [
            // Start point
            node.x() + node.width() / 2,
            node.y() + node.height(),
            // End point
            node.x() + node.width() / 2,
            node.y() + node.height() + this.grid.height * 2
          ],
          stroke: this.colors.black,
          strokeWidth: 1,
          lineCap: "round",
          lineJoin: "round",
          name: "operator"
        });
      }
    },
    // Updates operator position by node
    updateOperator(node, operator) {
      if (
        !this.specialElements.includes(node.name()) &&
        !this.hasExplicitErrorHandling(node)
      ) {
        operator.points([
          // Start point
          node.x() + node.width() / 2,
          node.y() + node.height(),
          // End point
          node.x() + node.width() / 2,
          node.y() + node.height() + this.grid.height * 2
        ]);
      }
    },
    // Creates the label text
    createTextLabel(node, content) {
      let textNode = new Konva.Text({
        x: node.x() + node.width() + 5,
        y: node.y() - 6 + node.height() / 2,
        name: "textLabel",
        text: content,
        fontSize: 12,
        lineHeight: 1.2,
        fontFamily: "Poppins, Helvetica, sans-serif",
        fill: "#000",
        width: this.grid.width * 2 - 5
      });
      this.checkTextLabelHeight(textNode);
      return textNode;
    },
    // Creates the type text
    createTextType(node, content) {
      return new Konva.Text({
        x: node.x() + node.width() + 5,
        y: node.y() - 6 + node.height() / 2,
        name: "textType",
        text: content,
        fontSize: 11,
        fontStyle: "italic",
        lineHeight: 1.2,
        fontFamily: "Poppins, Helvetica, sans-serif",
        fill: "#B5B5C3",
        width: this.grid.width * 3
      });
    },
    // Create states for node
    createStates(node) {
      let $nodeStates = $("#node-states");
      $nodeStates.append(
        `<div id="state-${node.attrs.data.hash}" class="position-absolute">
            <i class="state-success fal fa-circle-check text-success bg-white rounded-circle"></i>
            <i class="state-error fal fa-circle-exclamation text-danger bg-white rounded-circle"></i>
        </div>`
      );
      this.getSuccessStateFromNode(node).hide();
      this.getErrorStateFromNode(node).hide();
    },
    // Updates text position and possibly text content
    updateText(group, newTextLabel = undefined, newTextType = undefined) {
      let textType = this.getTextTypeFromGroup(group);
      let textLabel = this.getTextLabelFromGroup(group);
      let node = this.getNodeFromGroup(group);

      if (newTextLabel !== undefined) {
        textLabel.text(newTextLabel);
        this.checkTextLabelHeight(textLabel);
      }
      textLabel.x(node.x() + node.width() + 5);
      textLabel.y(node.y() + node.height() / 2 - textLabel.height());

      if (newTextType !== undefined) textType.text(newTextType);
      textType.x(node.x() + node.width() + 5);
      textType.y(node.y() + node.height() / 2);

      if (!textLabel.text().length)
        textType.y(textType.y() - textType.height() / 2);
      this.stage.batchDraw();
    },
    // Check if text label is too long and shorten it
    checkTextLabelHeight(textNode) {
      if (textNode.height() < 46) return;
      let text = textNode.text().slice(0, -1);
      textNode.text(text);

      if (textNode.height() < 46) {
        text = text.slice(0, -2);
        text += "...";
        textNode.text(text);
        return;
      }

      this.checkTextLabelHeight(textNode);
    },
    // Load canvas from fetched data :: coming soon
    async loadCanvas() {
      addEventToLoadingQueue({ key: "loadWorkflow" });

      let triggerGroup = new Konva.Group({ id: "trigger-group", x: 0, y: 0 });
      let dropzone = await this.createDropZone();
      triggerGroup.add(dropzone);
      this.layers.main.add(triggerGroup);

      let elements = this.workflow.configured_elements.sort(
        (a, b) => a.absolute_y - b.absolute_y
      );

      for (const element of elements) {
        let position = {
          position: {
            x: element.x,
            y: element.y
          }
        };

        if (element.flow_element && element.flow_element.flow_element_exists) {
          await this.addElement(element.flow_element, position, element);
        } else {
          this.$swal.fire({
            title: this.$t("workflowElements.elementDoesNotLongerExists"),
            icon: "error"
          });
          await this.addElement(element, position, element);
        }
      }

      //Fire element click if route parameter is set
      if (typeof this.$route.params.workflow_element_hash !== "undefined") {
        let node = this.layers.main.findOne(
          el => el.attrs.data?.hash === this.$route.params.workflow_element_hash
        );
        node.fire("click");
      }

      if (this.triggers.getChildren().length < 2) {
        let element = this.library.find(el => el.name === "manually");
        await FlowElements.getConfig({ flowElement: element.name }).then(
          ({ data }) => {
            element.config = data;
          }
        );

        let position = {
          position: {
            x: 0,
            y: 100
          }
        };
        await this.addElement(element, position);
      }

      for (const trigger of this.process.trigger_processes ?? []) {
        let element = this.library.find(el => el.name === "workflow");
        let position = {
          position: {
            x: 0,
            y: 100
          }
        };
        await this.addElement(element, position, {
          label: trigger.name.de,
          save: false
        });
      }

      this.updateTriggers();
      this.centerElements();
      this.isLoadingCanvas = false;
      this.historyAdd();

      // At this point the flow is fully loaded, all elements are confgured and set.
      // Now we can allow saving / test of the workflow
      removeEventFromLoadingQueue({ key: "loadWorkflow" });
      this.$emit("ready");
    },
    getElementFromLibrary(name) {
      return this.library.find(e => e.name === name);
    },
    // Returns node from group
    getNodeFromGroup(group) {
      let nodes = group.getChildren(child => {
        return child.getClassName() === "Shape";
      });
      return nodes.length ? nodes[0] : undefined;
    },
    // Returns operator from group
    getOperatorFromGroup(group) {
      let operators = group.getChildren(child => {
        return child.getClassName() === "Line";
      });
      return operators.length ? operators[0] : undefined;
    },
    // Return connectorLine
    getConnectorLineFromNode(group, id) {
      let connectorLines = group.getChildren(child =>
        child.hasName("connectorLine")
      );
      group
        .getChildren(child => {
          return child.getClassName() === "Group";
        })
        .forEach(childGroup => {
          if (typeof id !== "undefined") {
            connectorLines.push(...this.getConnectorLineFromNode(childGroup));
          }
        });
      return connectorLines;
    },
    // Returns dropzone from group
    getDropzoneFromGroup(group) {
      let dropzones = group.getChildren(child => {
        return child.hasName("dropzone");
      });
      return dropzones.length ? dropzones[0] : undefined;
    },
    // Returns type from group
    getTextTypeFromGroup(group) {
      let nodes = group.getChildren(child => {
        return child.hasName("textType");
      });
      return nodes.length ? nodes[0] : undefined;
    },
    // Returns label from group
    getTextLabelFromGroup(group) {
      let nodes = group.getChildren(child => {
        return child.hasName("textLabel");
      });
      return nodes.length ? nodes[0] : undefined;
    },
    getSuccessStateFromNode(node) {
      return $(`#node-states #state-${node.id()} .state-success`).first();
    },
    getErrorStateFromNode(node) {
      return $(`#node-states #state-${node.id()} .state-error`).first();
    },
    // Sets shape events
    setShapeEvents(shape) {
      let me = this;
      // Generate clone to drag when drag starts
      shape.on("dragstart", () => {
        me.tooltipOptions.show = false;
        shape.stopDrag();
        me.helperNodes.clone = shape.clone({ listening: false, opacity: 0.7 });
        me.clone.position({
          x: (shape.getAbsolutePosition().x - me.stage.x()) * (1 / this.scale),
          y: (shape.getAbsolutePosition().y - me.stage.y()) * (1 / this.scale)
        });
        me.clone.off("dragstart");
        me.layers.shadows.add(me.clone);
        me.setCloneEvents();
        me.clone.startDrag();
        me.clone.moveToTop();
      });
      // Sets node to active node on click
      shape.on("click", e => {
        if (me.preventNodeClick) {
          bus.$emit("nodeSelected", shape);
          return;
        }
        if (me.currentNode) {
          me.currentNode.shadowEnabled(false);
          me.currentNode.stroke(me.colors.black);
          let operators = me.layers.main.find("Line, Arrow");
          operators.forEach(currentOperator => {
            currentOperator.stroke(me.colors.black);
            currentOperator.fill(me.colors.black);
          });
        }
        if (me.currentNode === shape && e.evt?.button !== 2) {
          me.currentNode = undefined;
          me.stage.batchDraw();
          me.updatePreview();
          return;
        }
        shape.shadowEnabled(true);
        shape.stroke(me.colors.primary);
        if (
          !shape.findAncestor(this.operatorGroupSelector) ||
          this.specialElements.includes(shape?.name()) ||
          this.isExplicitErrorHandlingActive(shape)
        ) {
          let operators = shape.getParent().find("Line, Arrow");
          operators.forEach(operator => {
            operator.stroke(me.colors.primary);
            operator.fill(me.colors.primary);
          });
        }
        me.currentNode = shape;
        shape.getParent().moveToTop();
        if (shape.getParent().getParent().id() === "trigger-group") {
          shape.getParent().getParent().findOne(".dropzone").moveToTop();
        }
        me.stage.batchDraw();
        me.updatePreview();
      });
      // Shows context menu when right clicked
      shape.on("contextmenu", e => {
        this.tooltipOptions.show = false;
        e.evt.preventDefault();
        me.contextNode = shape;
        let menu = me.$refs.contextMenu.$el;
        menu.style.display = "initial";
        menu.style.top = me.stage.getPointerPosition().y + 40 + "px";
        menu.style.left = me.stage.getPointerPosition().x + 4 + "px";
      });
      // Shows hover tooltip
      shape.on("mouseover", function () {
        if (me.preventNodeClick) {
          shape.stroke("#ff0000");
        }
        clearTimeout(me.tooltipOptions.timeout);
        me.setTooltipPosition(shape);
        me.hoverNode = shape;
        me.tooltipOptions.show = true;
        me.tooltipKey++;
      });
      // Hides hover tooltip
      shape.on("mouseleave", function () {
        if (me.preventNodeClick) {
          shape.stroke("#9babbd");
        }
        me.tooltipOptions.timeout = setTimeout(function () {
          me.tooltipOptions.show = false;
        }, 500);
      });
    },
    // Sets drag events for clone
    setCloneEvents() {
      let me = this;
      // Get possible drop position
      this.clone.on("dragmove", e => {
        me.dragMoveHandler(e.evt);
        me.tooltipOptions.show = false;
      });
      // Drop clone/original element on new position
      this.clone.on("dragend", e => {
        let result = me.dragMoveHandler(e.evt);
        me.unsetDropzone();
        if (!me.mouseOverCanvas || !result) {
          me.clone.destroy();
          return;
        }
        const id = me.clone.id();
        let original = me.layers.main.findOne("#" + id);
        // Actual moving
        me.moveNode(original, result.following, result.parent, result.position);
        me.clone.destroy();
      });
    },
    // Update operator and texts after node moved
    nodeMoved(node) {
      let group = node.getParent();
      let operator = this.getOperatorFromGroup(group);
      this.updateOperator(node, operator);
      this.updateText(group);
      this.updateStates(node);
      this.updateDropzone(node);
      if (
        this.specialElements.includes(node.name()) ||
        this.hasExplicitErrorHandling(node)
      )
        this.updateSpecialGroup(node.getParent());
    },
    updateDropzone(node) {
      let group = node.getParent();
      let dropzone = group.getChildren().find(c => c.hasName("dropzone"));
      if (!dropzone) return;
      if (this.operatorGroupSelector.includes(group.name())) {
        if (
          node.attrs.data.next_hash ||
          node.getParent().findAncestor(this.operatorGroupSelector)
        ) {
          dropzone.x(node.x() - 7.5 + this.grid.width / 2);
          dropzone.y(node.y() + this.getNodeHeight(node) - 50 + 18.5);
        } else {
          dropzone.x(node.x() - 7.5 + this.grid.width / 2);
          dropzone.y(node.y() + this.getNodeHeight(node));
        }
      } else {
        if (
          node.attrs.data.next_hash ||
          node.findAncestor(this.operatorGroupSelector)
        ) {
          dropzone.position({
            x: node.x() + this.getNodeWidth(node) / 2 - 7.5,
            y: node.y() + this.getNodeHeight(node) - 50 + 18.5
          });
        } else {
          dropzone.position({
            x: node.x() + this.getNodeWidth(node) / 2 - 7.5,
            y: node.y() + this.getNodeHeight(node)
          });
        }
      }
      dropzone.moveToTop();
      dropzone.visible(!node.attrs.data?.is_endpoint);
    },
    updateStates(node) {
      let $nodeState = $("#state-" + node.id());
      let position = node.getAbsolutePosition();
      $nodeState.css(
        "top",
        position.y + (node.height() - 16) * this.scale + "px"
      );
      $nodeState.css(
        "left",
        position.x + (node.width() - 16) * this.scale + "px"
      );
      $nodeState.css("transform", `scale(${this.scale})`);
      let $success = this.getSuccessStateFromNode(node);
      let $error = this.getErrorStateFromNode(node);
      if (node.attrs.data.missingFields === 0) {
        $success.show();
        $error.hide();
      } else if (node.attrs.data.missingFields > 0) {
        $success.hide();
        $error.show();
      } else {
        $success.hide();
        $error.hide();
      }
    },
    updateAllStates() {
      this.layers.main
        .find(el => el.getClassName() === "Shape" && el.attrs.data?.hash)
        .forEach(node => {
          this.updateStates(node);
        });
    },
    // Centers workflow
    centerElements() {
      let center =
        (5 * Math.floor(this.stageWidth / this.grid.width) * this.grid.width) /
          12 -
        this.grid.width / 2;
      this.layers.main.getChildren().forEach(group => {
        if (group === this.triggers) return;
        let node = this.getNodeFromGroup(group);
        node.x(center);
      });
      this.layers.main
        .find(n => n.attrs.data?.hash)
        .forEach(node => {
          this.nodeMoved(node);
        });
      this.updateTriggers();
      this.stage.batchDraw();
      this.updatePreview();
    },
    // Calculates position for tooltip
    setTooltipPosition(node) {
      const pos = node.getAbsolutePosition(this.layers.main);
      const mainDivPos = $("#editorMainDiv")[0].getBoundingClientRect();
      this.tooltipOptions.position = {
        x: pos.x / (1 / this.scale) + mainDivPos.x - 10 + this.stage.x(),
        y:
          pos.y / (1 / this.scale) +
          mainDivPos.y +
          node.height() / 2 +
          this.stage.y()
      };
    },
    // Fired when mouse leaves tooltip
    tooltipLeaving() {
      let me = this;
      this.tooltipOptions.timeout = setTimeout(() => {
        me.tooltipOptions.show = false;
        me.tooltipOptions.edit = false;
      }, 500);
      setTimeout(function () {
        me.tooltipOptions.hovering = false;
      }, 500);
    },
    // Update new label text
    labelTextChanged(node) {
      let group = node.getParent();
      let label = resolveLocalizedString(node.attrs.data.planning.label);
      this.updateText(group, label);
      group.draw();
    },
    // Toggle end point flag, show/hide operator
    toggleEndpoint(node) {
      this.$refs.contextMenu.$el.style.display = "none";
      node.attrs.data.is_endpoint = !node.attrs.data.is_endpoint;
      let group = node.getParent();
      let dropzones = this.getDropzoneFromGroup(group);
      dropzones.visible(!node.attrs.data.is_endpoint);

      if (
        this.specialElements.includes(node.name()) ||
        this.hasExplicitErrorHandling(node)
      ) {
        let connectorLines = this.getConnectorLineFromNode(
          group,
          group.attrs.id
        );
        this.getDropzoneFromGroup(group).visible(!node.attrs.data.is_endpoint);
        connectorLines.forEach(connectorLine => {
          connectorLine.visible(!node.attrs.data.is_endpoint);
        });

        // Extra handling of explicit error handling nodes to correctly display the endpoint
        if (
          this.hasExplicitErrorHandling(node) &&
          (node.attrs.data.error[0].value === undefined ||
            node.attrs.data.error[0].value === false)
        ) {
          let condition = group.children.find(
            child => child.attrs.name === "condition"
          );

          condition.children.forEach(child => {
            if (child.attrs.name !== "label-group") {
              child.visible(false);
            }
          });
          // Hide first children of condition, otherwise a line will be displayed
          condition.children[0].visible(!node.attrs.data.is_endpoint);
        }
        return;
      }

      let operator = this.getOperatorFromGroup(group);
      operator.visible(!node.attrs.data.is_endpoint);

      let branch = node.findAncestor(".branch-group");
      let dataKey = "configuration";
      let elementName = "conditions";

      if (!branch) {
        branch = node.findAncestor(".exception-group");
        dataKey = "error";
        elementName = "exceptionGroups";
      } else {
        let endPoints = branch.find(
          n =>
            !this.specialElements.includes(n.name()) &&
            n.attrs?.data?.is_endpoint
        );
        let branchNode = this.getNodeFromGroup(branch);
        let conditions = Object.keys(
          branchNode.attrs.data[dataKey].find(el => el.name === elementName)
            .value
        );
        if (
          endPoints.length >= conditions.length &&
          branchNode?.attrs.data?.next_hash.length
        )
          node.attrs.data.is_endpoint = false;
        this.updateBranch(branch, dataKey, elementName);
      }

      this.stage.batchDraw();
      this.updatePreview();
    },
    // Delete node
    deleteNode(node) {
      //get parent group of node
      let groups = node
        .getParent()
        .getChildren(c => c.getClassName() === "Group");
      groups.forEach(group => {
        let id = group.id().split("-")[1];
        // find nodes where group_hash equals group id
        let children = this.nodes.filter(n => n.attrs.data.group_hash === id);
        children.forEach(child => {
          this.deleteNode(child);
        });
      });
      let isOperatorGroupChild = node
        .getParent()
        .findAncestors(this.operatorGroupSelector, false);
      this.$refs.contextMenu.$el.style.display = "none";
      if (
        node.name() === "manually" &&
        this.layers.main.find(".manually").length < 2
      ) {
        this.$toast.fire({
          icon: "warning",
          title: "Es muss mindestens einen manuellen Trigger geben."
        });
        return;
      }
      if (node.attrs.type !== "trigger") {
        if (!isOperatorGroupChild.length) {
          // Move up elements below
          this.moveNodesUp(node, node.getParent().getParent());
        }
        // Get parent element
        let parent = this.layers.main.findOne(
          n => n.attrs.data?.next_hash === node.id()
        );
        // Get following element if set
        let child = node.attrs.data.next_hash
          ? this.layers.main.findOne(
            n => n.attrs.id === node.attrs.data.next_hash
            )
          : undefined;
        if (parent) {
          let grandParent = parent.getParent().getParent();
          if (grandParent.id() === "trigger-group") {
            // If parent is part of trigger group, update differently
            grandParent
              .getChildren(c => c.getClassName() === "Group")
              .forEach(group => {
                // Update next_hash of each trigger
                let node = this.getNodeFromGroup(group);
                node.attrs.data.next_hash = child ? child.id() : "";
              });
            // Update triggers for correct dropzone position
            this.updateTriggers();
          } else {
            // Update only parent's next_hash
            parent.attrs.data.next_hash = child ? child.id() : "";
            // Update dropzone
            this.updateDropzone(parent);
          }
        }
      }
      // Delete states
      $(`#node-states #state-${node.id()}`).first().remove();
      if (
        this.specialElements.includes(node.name()) ||
        (this.hasExplicitErrorHandling(node) &&
          this.isExplicitErrorHandlingActive(node))
      ) {
        node
          .getParent()
          .find(n => n.attrs.data?.hash)
          .forEach(toDelete => {
            // Delete children's states
            $(`#node-states #state-${toDelete.id()}`).first().remove();
          });
      }
      // Actually destroy node and its group
      node.getParent().destroy();
      if (isOperatorGroupChild.length) {
        isOperatorGroupChild.forEach(group => this.updateSpecialGroup(group));
        isOperatorGroupChild
          .reverse()
          .forEach(group => this.updateSpecialGroup(group));
      }
      // Update triggers if node was a trigger
      if (node.attrs.type === "trigger") this.updateTriggers();
      // Unset variables where node was active
      if (this.contextNode === node) this.contextNode = undefined;
      if (this.hoverNode === node) this.hoverNode = undefined;
      if (this.currentNode === node) this.currentNode = undefined;
      this.stage.batchDraw();
      this.nodes.splice(this.nodes.indexOf(node), 1);
      // Update preview
      this.updatePreview();
      this.historyAdd();
      this.removeOutputValues(node.attrs.data.hash);
    },
    removeOutputValues(hash) {
      let outputVariableSet = this.customVariablesSet("outputValues");
      let variables = outputVariableSet.variables.filter(
        v => !v.id.includes(hash)
      );
      const payload = {
        ...outputVariableSet,
        variables: variables,
        merge: false
      };
      this[SET_CUSTOM_VARIABLES](payload);
    },
    loadInactiveNodes(node) {
      if (node.attrs.data.active === false) {
        node.opacity(0.3);
        let deactivateLabelText = resolveLocalizedString(
          node.attrs.data.planning.label ?? node.attrs.label
        );
        this.updateText(
          node.getParent(),
          "[" + this.$t("general.inactive") + "] " + deactivateLabelText
        );
      }
    },
    loadActiveNodes(node) {
      if (node.attrs.data.active) {
        node.opacity(1);
        let activateLabelText = resolveLocalizedString(
          node.attrs.data.planning.label ?? node.attrs.label
        );
        this.updateText(node.getParent(), activateLabelText);
      }
    },
    deactivateNodeGroup(node, type) {
      let groups = node
        .getParent()
        .getChildren(c => c.getClassName() === "Group");
      groups.forEach(group => {
        let id = group.id().split("-")[1];
        // find nodes where group_hash equals group id
        let children = this.nodes.filter(n => n.attrs.data.group_hash === id);
        children.forEach(child => {
          this.deactivateNodeGroup(child, type);
          if (type === "deactivate") {
            child.attrs.data.active = false;
            this.loadInactiveNodes(child);
            return;
          }
          child.attrs.data.active = true;
          this.loadActiveNodes(child);
        });
      });
    },
    deactivateNode(node) {
      let group = node.getParent();
      let activeState = node.attrs.data.active ?? true;
      let deactivateLabelText = resolveLocalizedString(
        node.attrs.data.planning.label ?? node.attrs.label
      );
      let activeLabelText = resolveLocalizedString(
        node.attrs.data.planning.label
      );

      if (activeState) {
        node.attrs.data.active = false;
        node.opacity(0.3);
        this.updateText(
          group,
          "[" + this.$t("general.inactive") + "] " + deactivateLabelText
        );
        this.deactivateNodeGroup(node, "deactivate");
      } else {
        node.attrs.data.active = true;
        this.updateText(group, activeLabelText);
        node.opacity(1);
        this.deactivateNodeGroup(node);
      }
      this.$refs.contextMenu.$el.style.display = "none";
    },
    updateTriggers() {
      if (!this.triggers) {
        return;
      }
      // Get groups of triggers, filters dropzone
      let triggerGroups =
        this.triggers.getChildren(c => c.getClassName() === "Group") ?? [];
      let x =
        (5 * Math.floor(this.stageWidth / this.grid.width) * this.grid.width) /
          12 -
        (triggerGroups.length * this.grid.width * 3) / 2 +
        this.grid.width;
      // Set x, y, width & height of trigger group
      this.triggers.x(x);
      this.triggers.y(100);
      this.triggers.width(triggerGroups.length * this.grid.width * 3);
      this.triggers.height(this.grid.height * 4);
      // Set next element check to false
      let hasNextElement = false;
      for (let i = 0; i < triggerGroups.length; i++) {
        // Get trigger from group
        let trigger = triggerGroups[i].findOne("Shape");
        // Set trigger x and y
        trigger.x(i * this.grid.width * 3);
        trigger.y(0);
        // Update label positions
        this.nodeMoved(trigger);
        // Update lines
        this.updateTriggerOperator(trigger);
        // Set to true if trigger has next_hash
        if (!hasNextElement && trigger.attrs.data.next_hash) {
          hasNextElement = true;
        }
      }
      // Find and update dropzone position
      let dropzone = this.triggers.findOne(".dropzone");
      dropzone.position({
        x: this.triggers.width() / 2 - 7.5 - this.grid.width,
        y: this.triggers.height() - (hasNextElement ? 15 + 18.5 : 0)
      });
      dropzone.moveToTop();
      this.stage.batchDraw();
    },
    updateTriggerOperator(trigger) {
      let operator = this.getOperatorFromGroup(trigger.getParent());
      operator.points([
        // Start point at bottom of trigger element
        trigger.x() + trigger.width() / 2,
        trigger.y() + trigger.height(),
        // Line down
        trigger.x() + trigger.width() / 2,
        trigger.y() + trigger.height() + this.grid.height,
        // Line to middle of trigger group
        this.triggers.width() / 2 - this.grid.width,
        trigger.y() + trigger.height() + this.grid.height,
        // Line down to bottom of trigger group
        this.triggers.width() / 2 - this.grid.width,
        trigger.y() + trigger.height() + this.grid.height * 2
      ]);
      operator.draw();
    },
    updateParent(node, parent) {
      let hash = node.id();
      if (parent.attrs?.type === "trigger")
        parent = parent.getParent().getParent();
      if (parent.id() === "trigger-group") {
        parent
          .getChildren(c => c.getClassName() === "Group")
          .forEach(triggerGroup => {
            let trigger = this.getNodeFromGroup(triggerGroup);
            trigger.attrs.data.next_hash = hash;
          });
        this.updateTriggers();
      } else if (
        parent.hasName("branch-group") ||
        parent.hasName("exception-group")
      ) {
        let branchNode = this.getNodeFromGroup(parent);
        branchNode.attrs.data.next_hash = hash;
      } else {
        parent.attrs.data.next_hash = hash;
      }
      this.updateDropzone(parent);
    },
    getNodeHeight(node, forceLargeHeight = false, forceSmallHeight = false) {
      let getLargeHeight =
        this.specialElements.includes(node?.name()) ||
        (this.hasExplicitErrorHandling(node) &&
          this.isExplicitErrorHandlingActive(node)) ||
        forceLargeHeight;

      if (forceSmallHeight) getLargeHeight = false;

      if (getLargeHeight) {
        let container = undefined;
        if (node.getParent().hasName("loop-group")) {
          container = node.getParent().findOne(".loop-start");
        } else {
          container = node.getParent().findOne("Group");
        }
        if (!container) return 0;
        return container.height() + 2 * this.grid.height;
      } else {
        return this.grid.height * 4;
      }
    },
    getNodeWidth(node) {
      if (
        (this.specialElements.includes(node?.name()) &&
          !node.hasName("loop")) ||
        (this.hasExplicitErrorHandling(node) &&
          this.isExplicitErrorHandlingActive(node))
      ) {
        let groups = node
          .getParent()
          .getChildren(child => child.getClassName() === "Group");
        let width = 0;
        groups.forEach(group => (width += group.width()));
        return width;
      } else if (node.hasName("loop")) {
        let loopStart = node
          .getParent()
          .getChildren(child => child.hasName("loop-start"))[0];
        return loopStart?.width() ?? this.grid.width;
      } else {
        return this.grid.width;
      }
    },
    moveNode(node, following, parent, position) {
      let nodeContainer = node.getParent()?.getParent();
      let targetContainer = parent?.getParent()?.getParent();
      if (parent === this.triggers) {
        targetContainer = this.layers.main;
      }

      this[
        `moveFrom${nodeContainer?.getClassName() ?? "New"}To${
          targetContainer?.getClassName() ?? "Layer"
        }`
      ](node, following, parent, position);
      this.updateAllStates();
      this.stage.batchDraw();
      // Update preview
      this.updatePreview();
      if (!this.isLoadingCanvas) {
        // Update history
        this.historyAdd();
      }
    },
    // Adds element from element library to nodes
    moveFromNewToLayer(node, following, parent) {
      let nodeGroup = node.getParent();
      this.layers.main.add(nodeGroup);

      if (this.isLoadingCanvas) return;

      // Set next_hash of parent to node's id
      if (parent && !parent.hasName("dropzone")) {
        this.updateParent(node, parent);
      }
      // If drop position has a following node, set next_hash to it's id, else ""
      node.attrs.data.next_hash = following ? following.id() : "";
      this.nodeMoved(node);
      // Move nodes below down
      this.moveNodesDown(node, this.layers.main);
    },
    // Adds element from element library to node group eg. branch-group", "loop-group"
    moveFromNewToGroup(node, following, parent, position) {
      let targetGroup = parent.getParent();
      if (!parent.hasName("dropzone")) {
        // If parent is not a dropzone, grandparent is the branch group
        targetGroup = targetGroup.getParent();
      }
      // Add node to group and move to position
      targetGroup.add(node.getParent());
      node.position(position);
      // Set group_hash
      node.attrs.data.group_hash = targetGroup.id().split("-")[1];

      if (this.isLoadingCanvas) return;

      // Set next_hash of parent to node's id
      if (parent && !parent.hasName("dropzone")) {
        this.updateParent(node, parent);
      }
      // If drop position has a following node, set next_hash to it's id, else ""
      node.attrs.data.next_hash = following ? following.id() : "";

      // Move condition elements down
      this.moveNodesDown(node, targetGroup);

      // Update operator group
      let operatorGroup = targetGroup.getParent();
      this.updateSpecialGroup(operatorGroup);
      operatorGroup
        .findAncestors(this.operatorGroupSelector, false)
        .reverse()
        .forEach(group => this.updateSpecialGroup(group));

      if (
        this.specialElements.includes(node.name()) ||
        this.hasExplicitErrorHandling(node)
      ) {
        let operatorGroup = node.getParent();
        this.updateSpecialGroup(operatorGroup);
        operatorGroup
          .findAncestors(this.operatorGroupSelector, false)
          .reverse()
          .forEach(group => this.updateSpecialGroup(group));
      }
    },
    // drag and drop elements from layer to other dropzones
    moveFromLayerToLayer(node, following, parent, position) {
      // Get current parent of node
      let nodeParent = this.layers.main.findOne(
        el => el.attrs?.data?.next_hash === node.id()
      );
      // Get current child of node
      let nodeChild = this.layers.main.findOne(
        el => el.id() === node.attrs.data.next_hash
      );
      // Set current parent's next_hash to child's hash or "" if unset
      if (nodeParent) nodeParent.attrs.data.next_hash = nodeChild?.id() ?? "";

      // Set next_hash of parent to node's id
      if (parent && !parent.hasName("dropzone")) {
        this.updateParent(node, parent);
      }
      // If drop position has a following node, set next_hash to it's id, else ""
      node.attrs.data.next_hash = following ? following.id() : "";

      // Move nodes below old position up
      this.moveNodesUp(node, this.layers.main);
      // Adapt new position y to moving up nodes below
      if (position.y > node.y()) position.y -= this.getNodeHeight(node);
      // Move node to new position
      node.position(position);
      this.nodeMoved(node);
      // Move nodes below new position down
      this.moveNodesDown(node, this.layers.main);

      node.attrs.data.is_endpoint = false;
    },
    // drag and drop elements from layer to other dropzones within node group
    moveFromGroupToGroup(node, following, parent, position) {
      let fromContainer = node.getParent().getParent();
      let fromParent = fromContainer.findOne(
        el => el.attrs.data?.next_hash === node.id()
      );
      if (fromParent) {
        fromParent.attrs.data.next_hash = node.attrs.data.next_hash;
      }
      node.attrs.data.next_hash = "";
      this.moveNodesUp(node, fromContainer);

      // Set next_hash of parent to node's id
      if (parent && !parent.hasName("dropzone")) {
        this.updateParent(node, parent);
      }
      // If drop position has a following node, set next_hash to it's id, else ""
      node.attrs.data.next_hash = following ? following.id() : "";

      let toContainer = parent.getParent();
      if (!parent.hasName("dropzone")) {
        // If parent is not a dropzone, grandparent is the container
        toContainer = toContainer.getParent();
      }
      node.getParent().moveTo(toContainer);
      // Set group_hash
      node.attrs.data.group_hash = toContainer.id().split("-")[1];
      // If node is moved in same group downwards,
      // prevent false position because those nodes
      // where moved upwars just before
      if (toContainer === fromContainer && node.y() < position.y) {
        position.y = position.y - this.getNodeHeight(node);
      }
      node.position(position);
      this.moveNodesDown(node, toContainer);

      node.attrs.data.is_endpoint = false;
      let fromOperatorGroup = fromContainer.getParent();
      let toOperatorGroup = toContainer.getParent();
      this.updateSpecialGroup(fromOperatorGroup);
      fromOperatorGroup
        .findAncestors(this.operatorGroupSelector, false)
        .reverse()
        .forEach(group => this.updateSpecialGroup(group));
      if (fromOperatorGroup !== toOperatorGroup) {
        this.updateSpecialGroup(toOperatorGroup);
        toOperatorGroup
          .findAncestors(this.operatorGroupSelector, false)
          .reverse()
          .forEach(group => this.updateSpecialGroup(group));
      }
    },
    // drag and drop elements from layer to other dropzones within node group
    moveFromLayerToGroup(node, following, parent, position) {
      let oldParent = this.layers.main.findOne(
        el => el.attrs.data?.next_hash === node.id()
      );
      if (oldParent) {
        oldParent.attrs.data.next_hash = node.attrs.data.next_hash;
      }
      // Set node next_hash to empty string
      node.attrs.data.next_hash = "";

      // Move nodes up below old position
      this.moveNodesUp(node, this.layers.main);

      // Set next_hash of parent to node's id
      if (parent && !parent.hasName("dropzone")) {
        this.updateParent(node, parent);
      }
      // If drop position has a following node, set next_hash to it's id, else ""
      node.attrs.data.next_hash = following ? following.id() : "";

      // Get container of target node
      let targetContainer = parent.getParent();
      if (!parent.hasName("dropzone")) {
        // If parent is not a dropzone, grandparent is the container
        targetContainer = targetContainer.getParent();
      }
      // Move node group to target container and move it
      node.getParent().moveTo(targetContainer);
      node.position(position);
      this.nodeMoved(node);
      // Set group_hash
      node.attrs.data.group_hash = targetContainer.id().split("-")[1];
      // Move nodes in target container down
      this.moveNodesDown(node, targetContainer);
      node.attrs.data.is_endpoint = false;
      // Update target branch
      let targetOperatorGroup = targetContainer.getParent();
      this.updateSpecialGroup(targetOperatorGroup);
      targetOperatorGroup
        .findAncestors(this.operatorGroupSelector, false)
        .reverse()
        .forEach(group => this.updateSpecialGroup(group));
    },
    // drag and drop elements from node group to layer
    moveFromGroupToLayer(node, following, parent, position) {
      let fromContainer = node.getParent().getParent();
      let fromOperatorGroup = fromContainer.getParent();
      let fromParent = fromContainer.findOne(
        el => el.attrs.data?.next_hash === node.id()
      );
      if (fromParent) {
        fromParent.attrs.data.next_hash = node.attrs.data.next_hash;
      }
      node.attrs.data.next_hash = "";
      this.moveNodesUp(node, fromContainer);

      // Set next_hash of parent to node's id
      if (parent && !parent.hasName("dropzone")) {
        this.updateParent(node, parent);
      }
      // If drop position has a following node, set next_hash to it's id, else ""
      node.attrs.data.next_hash = following ? following.id() : "";

      // Move node to new position and show operator
      let nodeGroup = node.getParent();
      nodeGroup.moveTo(this.layers.main);
      node.position(position);
      // Remove group_hash
      node.attrs.data.group_hash = "";
      this.updateSpecialGroup(fromOperatorGroup);
      fromOperatorGroup
        .findAncestors(this.operatorGroupSelector, false)
        .forEach(group => this.updateSpecialGroup(group));
      fromOperatorGroup
        .findAncestors(this.operatorGroupSelector, false)
        .reverse()
        .forEach(group => this.updateSpecialGroup(group));
      this.getOperatorFromGroup(nodeGroup)?.show();
      this.nodeMoved(node);
      // Move nodes below new position down
      this.moveNodesDown(node, this.layers.main);
      node.attrs.data.is_endpoint = false;
    },
    moveNodes(groups, delta) {
      groups.forEach(group => {
        let children = group.getChildren();

        children.forEach(child => {
          if (
            child.getClassName() === "Line" ||
            child.getClassName() === "Arrow"
          ) {
            let points = child.points();
            for (let i = 1; i < points.length; i += 2) {
              points[i] += delta;
            }
            child.points(points);
          } else {
            child.y(child.y() + delta);
          }
        });
      });
    },
    // Move down all nodes below given node
    moveNodesDown(node, container) {
      let groups = container.getChildren(
        child =>
          child.getClassName() === "Group" &&
          this.nodeGroups.includes(child.name()) &&
          this.getNodeFromGroup(child)?.y() >= node.y() &&
          this.getNodeFromGroup(child) !== node
      );
      let delta = this.getNodeHeight(node);
      this.moveNodes(groups, delta);
    },
    // Move up all nodes below given node
    moveNodesUp(node, container) {
      let groups = container.getChildren(
        group =>
          group !== node.getParent() &&
          group.getClassName() === "Group" &&
          this.nodeGroups.includes(group.name()) &&
          this.getNodeFromGroup(group)?.y() >=
            node.y() + this.getNodeHeight(node)
      );
      let delta = this.getNodeHeight(node);
      this.moveNodes(groups, -delta);
    },
    // Move given node group under node on layer
    moveNodesBelow(node, container, delta = 0) {
      let groups = container.getChildren(
        child =>
          child !== node.getParent() &&
          child.getClassName() === "Group" &&
          this.getNodeFromGroup(child)?.y() > node.y() &&
          this.getNodeFromGroup(child) !== node
      );
      this.moveNodes(groups, delta);
    },
    createPreview() {
      let boundaries = this.getWorkflowBoundaries();
      // Init new stage for preview
      this.preview.stage = new Konva.Stage({
        container: "konva-preview",
        width: boundaries.width * 0.25,
        height: boundaries.height * 0.25,
        x: -boundaries.x,
        y: -boundaries.y
      });
      // Clone main layer for preview stage
      this.preview.layer = this.layers.main.clone({ listening: false });
      this.preview.stage.add(this.preview.layer);
      // Scale preview down to 25% of original size
      this.preview.stage.scale({ x: 0.25, y: 0.25 });
      // Set initial visibility state
      this.preview.stage.visible(this.toolbarOptions.showMap);
      // Create new layer for viewport rectangle
      this.preview.layerRect = new Konva.Layer();
      this.preview.stage.add(this.preview.layerRect);
      let me = this;
      // Create the viewport rect
      let rect = new Konva.Rect({
        x: 0,
        y: 0,
        width: this.container.width,
        height: this.container.height,
        stroke: this.colors.primary,
        strokeWidth: 3,
        draggable: true
      });
      // Register event handler for dragging -> move stage synchronously
      rect.on("dragstart", function () {
        rect.moveToTop();
        document.body.style.cursor = "grabbing";
      });
      rect.on("dragmove", function () {
        document.body.style.cursor = "grabbing";
        rect.moveToTop();
        me.stage.position({ x: -rect.x() * me.scale, y: -rect.y() * me.scale });
        me.stage.batchDraw();
      });
      rect.on("dragend", function () {
        rect.moveToTop();
        document.body.style.cursor = "grab";
      });
      rect.on("mouseover", function () {
        document.body.style.cursor = "grab";
      });
      rect.on("mouseout", function () {
        document.body.style.cursor = "default";
      });
      // Add rect to its layer and move to top
      this.preview.layerRect.add(rect);
      this.preview.layerRect.moveToTop();
      // Call update rect method
      this.updatePreviewRect();
      // Draw preview
      this.preview.stage.batchDraw();
    },
    updatePreview() {
      let boundaries = this.getWorkflowBoundaries();
      // Adapt stage width to preview
      this.preview.stage.width(boundaries.width * 0.25 * (1 / this.scale));
      this.preview.stage.height(boundaries.height * 0.25 * (1 / this.scale));
      this.preview.stage.x(
        (-boundaries.x + this.stage.x()) * 0.25 * (1 / this.scale)
      );
      this.preview.stage.y(
        (-boundaries.y + this.stage.y()) * 0.25 * (1 / this.scale)
      );

      // Destroy old preview layer
      this.preview.layer.destroy();
      // Clone current main layer
      this.preview.layer = this.layers.main.clone({ listening: false });
      this.preview.stage.add(this.preview.layer);
      // Move viewport rect to top
      this.preview.layerRect.moveToTop();
      // Draw preview
      this.preview.stage.batchDraw();
    },
    updatePreviewRect() {
      // Get viewport rect
      let rect = this.preview.layerRect.getChildren()[0];
      // Set new viewport dimensions
      rect.width(this.container.width * (1 / this.scale));
      rect.height(this.container.height * (1 / this.scale));
      // Move to current position
      rect.x(-this.stage.x() / this.scale);
      rect.y(-this.stage.y() / this.scale);
      rect.draggable(true);
      // Move to top and draw
      this.preview.layerRect.moveToTop();
      this.preview.layerRect.batchDraw();
    },
    toggleCriticalPath() {
      this.toolbarOptions.showCriticalPath =
        !this.toolbarOptions.showCriticalPath;

      this.showHideCriticalPath();
    },
    showHideCriticalPath() {
      for (const node of this.nodes) {
        const errorHandling = node.attrs.data.error.find(
          el => el.name === "error_handling"
        );

        const opacity =
          this.toolbarOptions.showCriticalPath &&
          (!errorHandling || errorHandling.value !== "process_aborts")
            ? 0.3
            : 1;

        node.opacity(opacity);

        if (
          !node.findAncestor(this.operatorGroupSelector) ||
          this.specialElements.includes(node?.name())
        ) {
          let operators = node.getParent().find("Line, Arrow");
          operators.forEach(operator => {
            operator.opacity(opacity);
          });
        }
      }
    },
    toggleMap() {
      this.toolbarOptions.showMap = !this.toolbarOptions.showMap;
      this.preview.stage.visible(this.toolbarOptions.showMap);
      this.updatePreview();
    },
    centerView() {
      this.stage.position({ x: 0, y: 0 });
      this.updateAllStates();
      this.stage.batchDraw();
      this.updatePreviewRect();
    },
    zoom(scaleDiff) {
      if (scaleDiff > 0 && this.scale.toFixed(1) >= 2) return;
      else if (scaleDiff < 0 && this.scale.toFixed(1) <= 0.6) return;
      this.toolbarOptions.scale += scaleDiff;
      this.stage.scale({ x: this.scale, y: this.scale });
      this.updatePreview();
      if (scaleDiff < 0) this.stage.position({ x: 0, y: 0 });
      this.updateAllStates();
      this.updatePreviewRect();
    },
    share() {
      this.centerView();
      let prevScale = this.scale;
      let prevPosition = this.stage.position();
      this.zoom(1 - this.scale);
      this.stage.position({ x: 0, y: 0 });
      let background = new Konva.Rect({
        x: this.stage.x(),
        y: this.stage.y(),
        width: this.stage.width(),
        height: this.stage.height(),
        fill: "#ffffff"
      });
      this.layers.main.moveToTop();
      let boundaries = this.getWorkflowBoundaries();
      let dataUrl = this.stage.toDataURL({
        x: boundaries.x,
        y: boundaries.y,
        width: boundaries.width,
        height: boundaries.height
      });
      let link = document.createElement("a");
      link.download =
        (resolveLocalizedString(this.process.name) || "Workflow") + ".png";
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      background.destroy();
      this.zoom(prevScale - 1);
      this.stage.position(prevPosition);
    },
    getWorkflowBoundaries() {
      let sortedX = this.layers.main
        .find(el => el.getClassName() === "Shape")
        .sort((a, b) => a.getAbsolutePosition().x - b.getAbsolutePosition().x);
      let sortedY = this.layers.main
        .find(el => el.getClassName() === "Shape")
        .sort((a, b) => a.getAbsolutePosition().y - b.getAbsolutePosition().y);
      if (!sortedX.length || !sortedY.length) return {};

      let xMin = sortedX[0].getAbsolutePosition().x - 3 * this.grid.width;

      let nodeXMax = sortedX[sortedX.length - 1];

      let xMax =
        nodeXMax.getAbsolutePosition().x +
        this.getNodeWidth(nodeXMax) +
        3 * this.grid.width;

      let yMin = sortedY[0].getAbsolutePosition().y - 2 * this.grid.height;

      let nodeYMax = sortedY[sortedY.length - 1];

      let yMax =
        nodeYMax.getAbsolutePosition().y +
        this.getNodeHeight(nodeYMax) +
        2 * this.grid.height;

      return {
        x: xMin,
        y: yMin,
        width: xMax - xMin,
        height: yMax - yMin
      };
    },
    clearAll() {
      this.$swal
        .fire({
          title: this.$t("workflowDesigner.clearDesignerTitle"),
          text: this.$t("workflowDesigner.clearDesignerText"),
          icon: "warning",
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonColor: this.colors.primary,
          // cancelButtonColor: this.colors.gray,
          cancelButtonText: this.$t("general.cancel"),
          confirmButtonText: this.$t("general.delete")
        })
        .then(result => {
          if (result.isConfirmed) {
            this.clearAllAction();
          }
        });
    },
    clearAllAction() {
      this.currentShape = undefined;
      let remove = [];
      this.layers.main.getChildren().forEach(group => {
        if (group.id() === "trigger-group") return;
        remove.push(group);
      });
      const removeLength = remove.length;
      for (let i = 0; i < removeLength; i++) {
        if (!remove[i]) continue;
        remove[i].destroy();
      }
      this.stage.x(0);
      this.stage.y(0);
      this.stage.batchDraw();
      this.historyAdd();
    },
    updateSpecialGroup(group) {
      switch (group.name()) {
        case "branch-group":
          this.updateBranch(group);
          break;
        case "loop-group":
          this.updateLoop(group);
          break;
        case "exception-group":
          this.updateBranch(group, "error", "exceptionGroups");
          break;
      }
      return group;
    },
    isOperatorGroup(group) {
      return (
        group.hasName("branch-group") ||
        group.hasName("loop-group") ||
        group.hasName("exception-group")
      );
    },
    historyAdd() {
      if (!this.layers.main) return;
      // Get children from main layer, slice() => copy without reference !important
      let children = this.layers.main
        .clone({ listening: false })
        .getChildren()
        .slice();
      // If history step is not the latest history entry, remove the newer ones
      if (this.historyStep > 0) {
        this.history.splice(0, this.historyStep);
        // Reorder history to start again from key 0
        this.history = [...this.history];
      }
      // Push children to position 0 in history array
      this.history.unshift(children);
      // Set current history step to latest
      this.historyStep = 0;
      // Cut history entries which are older than latest 50
      this.history.splice(50);
    },
    historyUndo() {
      // Move one step back in history
      this.historyStep++;
      if (
        !this.history[this.historyStep] ||
        this.historyStep > this.history.length - 1
      ) {
        // If this is no valid history entry return old step
        return this.historyStep--;
      }
      // Load selected history entry
      this.loadHistory();
    },
    historyRedo() {
      // If history step is latest yet, return
      if (this.historyStep === 0) return;
      // Move to next newer step
      this.historyStep--;
      // If this is no valid history entry return old step
      if (!this.history[this.historyStep]) return this.historyStep++;
      // Load selected history entry
      this.loadHistory();
    },
    loadHistory() {
      // Remove event listeners from current nodes
      this.layers.main
        .find(el => el.getClassName() === "Shape")
        .forEach(shape => {
          shape.off("dragstart");
          shape.off("click");
          shape.off("contextmenu");
          shape.off("mouseover");
          shape.off("mouseleave");
        });
      // Remove child from layer before destroying it
      this.layers.main.removeChildren();
      // Destroy layer
      this.layers.main.destroy();
      // Get children by selected history step
      let children = this.history[this.historyStep].slice();
      // Create new layer
      this.layers.main = new Konva.Layer();
      // Add children to layer
      this.layers.main.add(...children);
      // Add event listeners to newly added nodes
      this.layers.main
        .find(el => el.getClassName() === "Shape")
        .forEach(shape => {
          this.setShapeEvents(shape);
        });
      // Add layer to stage
      this.stage.add(this.layers.main);
      // Draw stage
      this.stage.batchDraw();
      this.updatePreview();
      this.currentNode = undefined;
    },
    getDefaultConditions(elementName) {
      if (elementName === "branch") {
        return [
          // First if
          {
            id: generateHash(),
            condition: {
              type: "group",
              operator: "and",
              children: [
                {
                  type: "condition",
                  field: "",
                  operator: "",
                  value: "",
                  valid: undefined
                }
              ],
              valid: undefined
            }
          },
          // Else
          {
            id: generateHash(),
            condition: null
          }
        ];
      } else {
        return [
          {
            id: generateHash(),
            value: true
          },
          {
            id: generateHash(),
            value: false
          }
        ];
      }
    },
    getDefaultExceptionConditions() {
      return [
        // First if
        {
          id: generateHash(),
          default: true,
          exceptions: null,
          first: true
        },
        // Else
        {
          id: generateHash(),
          default: true,
          exceptions: null,
          last: true
        }
      ];
    },
    openPhpEditor(node) {
      this.$refs.contextMenu.$el.style.display = "none";
      let code = node.attrs.data.configuration.find(f => f.name === "code");
      if (!code) return;
      this.currentNode = undefined;
      this.stage.batchDraw();
      this.modalAttributes = {
        node: node,
        field: code,
        configValues: [],
        class: "modal-code",
        title: this.$t("formHelper.Code")
      };
      this.modalComponent = CodeFieldHelper;
      this.$bvModal.show("editor-modal");
    },
    onCloseContextMenu() {
      this.$refs.contextMenu.$el.style.display = "none";
    },
    moveNodeStates() {
      $("#node-states")
        .children()
        .forEach(nodeState => {
          let id = $(nodeState).id().split("-")[1];
          this.layers.main.findOne(n => (n.attrs.data.hash = id));
        });
    },
    copyNodeWithChildren(node) {
      this.copyNode(node, true);
    },
    copyNode(node, copyGroup = false) {
      const dataCopy = this.copyNodeData(node);
      const conditionIds = this.getNodeConditionIds(node);
      let specialConditionIds = this.getSpecialConditionIds(node);
      let nextHashIds = this.nodes
        .filter(n => specialConditionIds.includes(n.attrs.data.group_hash))
        .map(n => n.attrs.data.hash);
      let specialExceptionGroups = this.getNodeExceptionGroups(node);
      const exceptionGroups = this.nodes
        .filter(n => specialExceptionGroups.includes(n.attrs.data.group_hash))
        .map(n => n.attrs.data.hash);

      const filteredNodes = this.filterNodesByConditionsAndHashes(
        this.nodes,
        node.id(),
        conditionIds,
        nextHashIds,
        exceptionGroups
      );

      this.deleteKeys(dataCopy);

      if (filteredNodes.length && dataCopy && copyGroup) {
        dataCopy.children = filteredNodes.map((child, index) =>
          this.copyChildNode(
            child,
            conditionIds,
            specialConditionIds,
            specialExceptionGroups,
            index
          )
        );
      }
      // this gets executed if you want to copy a element w/ explicit error handling
      if (
        node.attrs.data?.error[0]?.name === "explicitErrorHandling" &&
        !copyGroup
      ) {
        // remove everything but first and last condition - clear element conditions
        dataCopy.config.error.exceptionGroups =
          dataCopy.config.error?.exceptionGroups?.filter(
            error => error.first || error.last
          );

        dataCopy.children = [
          {
            active: true,
            name: "logging",
            is_endpoint: true,
            conditionIndex: 1,
            config: {
              input: {
                log_message: "{{error.defaultException.message}}"
              },
              configuration: {
                log_method: "log"
              }
            }
          }
        ];
      }

      this.$store.dispatch(ADD_CLIPBOARD_ELEMENT, dataCopy);
      this.$refs.contextMenu.$el.style.display = "none";
      this.$nextTick().then(() => {
        this.setDragInEvents();
      });
    },
    copyChildNode(
      node,
      parentConditionIds,
      parentNextHashIds,
      parentExceptionGroupIds,
      index
    ) {
      const dataCopy = this.copyNodeData(node);
      // copy original y in order to sort elements in right order when pasting
      dataCopy.original_y = index + node.y();
      const conditionIds = this.getNodeConditionIds(node);
      let specialConditionIds = this.getSpecialConditionIds(node);
      let nextHashIds = this.nodes
        .filter(n => specialConditionIds.includes(n.attrs.data.group_hash))
        .map(n => n.attrs.data.hash);
      let specialExceptionGroups = this.getNodeExceptionGroups(node);
      const exceptionGroups = this.nodes
        .filter(n => specialExceptionGroups.includes(n.attrs.data.group_hash))
        .map(n => n.attrs.data.hash);

      dataCopy.conditionIndex = this.getNodeConditionIndex(
        node,
        parentConditionIds,
        parentNextHashIds,
        parentExceptionGroupIds
      );

      const filteredNodes = this.filterNodesByConditionsAndHashes(
        this.nodes,
        node.id(),
        conditionIds,
        nextHashIds,
        exceptionGroups
      );

      this.deleteKeys(dataCopy);

      if (filteredNodes.length > 0 && dataCopy) {
        index++;
        dataCopy.children = filteredNodes.map((childNode, i) =>
          this.copyChildNode(
            childNode,
            conditionIds,
            specialConditionIds,
            specialExceptionGroups,
            index + i
          )
        );
      }

      return dataCopy;
    },
    copyNodeData(node) {
      return { ...workflowElementToData(node) };
    },
    getNodeConditionIds(node) {
      return node.attrs.data.configuration?.conditions?.map(n => n.id) ?? [];
    },
    getSpecialConditionIds(node) {
      if (["branch", "cache"].includes(node.name())) {
        return node.attrs.data.configuration
          .filter(entry => Array.isArray(entry.value))
          .map(entry => entry.value.map(n => n.id))
          .flat();
      }
      return [];
    },
    getNodeExceptionGroups(node) {
      return node.attrs.data.error
        .filter(entry => Array.isArray(entry.value))
        .map(entry => entry.value.map(n => n.id))
        .flat();
    },
    getNodeConditionIndex(
      node,
      parentConditionIds,
      parentNextHashIds,
      parentExceptionGroupIds
    ) {
      const groupHash = node.attrs.data.group_hash;

      const groupHashIndex = parentConditionIds.indexOf(groupHash);
      const nextHashIndex = parentNextHashIds.indexOf(groupHash);
      const exceptionGroupIndex = parentExceptionGroupIds.indexOf(groupHash);

      return groupHashIndex === -1
        ? nextHashIndex !== -1
          ? nextHashIndex
          : exceptionGroupIndex
        : groupHashIndex;
    },
    filterNodesByConditionsAndHashes(
      nodes,
      groupHash,
      conditionIds,
      nextHashIds,
      exceptionGroups
    ) {
      return nodes.filter(
        n =>
          n.attrs.data.group_hash === groupHash ||
          conditionIds.includes(n.attrs.data.group_hash) ||
          nextHashIds.includes(n.attrs.data.hash) ||
          (exceptionGroups && exceptionGroups.includes(n.attrs.data.hash))
      );
    },
    deleteKeys(dataCopy) {
      this.keysToDelete.forEach(key => {
        if (dataCopy[key]) {
          delete dataCopy[key];
        }
      });
    },
    getDataFromClipboard(element) {
      if (!element) return undefined;
      element = JSON.parse(JSON.stringify(element));
      if (
        this.specialElements.includes(element.name) &&
        element.config.configuration.conditions
      ) {
        element.config.configuration.conditions.forEach(condition => {
          condition.id = generateHash();
        });
      } else if (element.config.error?.explicitErrorHandling) {
        element.config.error.exceptionGroups.forEach(exceptionGroup => {
          exceptionGroup.id = generateHash();
        });
      }

      let elementChildren = element?.children ?? [];
      elementChildren.forEach((child, index) => {
        element.children[index] = this.getDataFromClipboard(child);
      });

      return element;
    },
    isBatchElement(node) {
      const isBatchField = Object.values(node?.attrs?.data?.configuration).find(
        f => {
          return f.name === "isBatchElement";
        }
      );
      if (isBatchField === undefined) {
        return false;
      }
      return isBatchField.value;
    },
    hasExplicitErrorHandling(node) {
      const hasExplicitErrorHandling = Object.values(
        node?.attrs?.data?.error ?? {}
      )?.find(f => {
        return f.name === "explicitErrorHandling";
      });
      return hasExplicitErrorHandling !== undefined;
    },
    isExplicitErrorHandlingActive(node) {
      const hasExplicitErrorHandling = Object.values(
        node?.attrs?.data?.error
      )?.find(f => {
        return f.name === "explicitErrorHandling";
      });

      return !!(
        hasExplicitErrorHandling &&
        hasExplicitErrorHandling.value !== undefined &&
        hasExplicitErrorHandling.value
      );
    },
    onAddExceptionBranch(payload) {
      let branch = payload.node.getParent();
      this.toggleExplicitErrorHandlingCheckbox(payload.node, true);
      this.addCondition(branch, payload.id);
      this.updateBranch(branch, "error", "exceptionGroups");
      branch
        .findAncestors(this.operatorGroupSelector)
        .forEach(b => this.updateSpecialGroup(b));
      branch
        .findAncestors(this.operatorGroupSelector)
        .reverse()
        .forEach(b => this.updateSpecialGroup(b));
      let operators = this.currentNode.getParent().find("Line");
      operators.forEach(operator => {
        operator.stroke(this.colors.primary);
      });
      this.stage.batchDraw();
    },
    onRemoveExceptionBranch(payload) {
      let branch = payload.node.getParent();

      if (!this.nodeHasExceptionBranches(payload.node)) {
        this.toggleExplicitErrorHandlingCheckbox(payload.node, false);
      }

      this.removeCondition(branch, payload.id);
      this.updateBranch(branch, "error", "exceptionGroups");
      branch
        .findAncestors(this.operatorGroupSelector)
        .forEach(b => this.updateSpecialGroup(b));
      branch
        .findAncestors(this.operatorGroupSelector)
        .reverse()
        .forEach(b => this.updateSpecialGroup(b));
      this.stage.batchDraw();
    },
    /**
     * add exception branch to a given group
     * @param group
     * @returns {Promise<*>}
     */
    async addExceptionBranch(group) {
      let node = this.getNodeFromGroup(group);
      let configuration = node.attrs.data.error;
      let conditionsConfig = configuration.find(
        el => el.name === "exceptionGroups"
      );

      if (this.nodeHasExceptionBranches(node)) {
        this.toggleExplicitErrorHandlingCheckbox(node, true);
      }

      // Set branch arms count
      let conditions = conditionsConfig?.value?.length
        ? conditionsConfig.value
        : this.getDefaultExceptionConditions();
      let firstCondition = conditions.find(el => el.first);
      conditionsConfig.value = conditions;
      // Set group config
      group.id("group-" + node.getAttr("data").hash);
      group.name("exception-group");
      let dropzone = this.createDropZone();
      group.add(dropzone);

      // Create branch arms and init their components
      conditions.forEach((condition, index) => {
        if (!this.isExplicitErrorHandlingActive(node) && index > 0)
          return false;
        let addDropzone = !condition.first;
        this.addCondition(group, condition.id, addDropzone);
      });

      this.toggleVisibilityOfAdditionalElementsFromDefaultCondition(
        group,
        firstCondition.id
      );
      this.updateBranch(group, "error", "exceptionGroups");
      if (node.getAttr("data").is_endpoint) {
        let connectorLines = this.getConnectorLineFromNode(
          group,
          group.attrs.id
        );
        dropzone.visible(!node.attrs.data.is_endpoint);
        connectorLines.forEach(connectorLine => {
          connectorLine.visible(!node.attrs.data.is_endpoint);
        });
      }
      return group;
    },
    /**
     * Add branches when activating the explicit error handling
     * If there are no existing additional conditions -> use default "catch all" condition
     * Otherwise use the existing conditions
     * @param payload
     */
    onActivateExplicitErrorHandling(payload) {
      let branch = payload.node.getParent();
      let initialHeight = this.getNodeHeight(payload.node, false, true);
      // get the first default condition
      let firstCondition = payload.node.attrs.data.error
        .find(el => el.name === "exceptionGroups")
        .value.find(val => val.first);
      // get the last default condition ("catch all")
      let lastCondition = payload.node.attrs.data.error
        .find(el => el.name === "exceptionGroups")
        .value.find(val => val.last);
      // get every existing additional condition (including the "catch all" condition)
      let existingConditions = payload.node.attrs.data.error
        .find(el => el.name === "exceptionGroups")
        .value.filter(val => val.id !== firstCondition.id);

      if (!existingConditions.length) {
        this.addCondition(branch, lastCondition.id);
      } else {
        for (let existingCondition of existingConditions) {
          this.addCondition(branch, existingCondition.id);
        }
      }

      let lastConditionNode = branch
        .getChildren()
        .find(el => el.id() === "condition-" + lastCondition.id);
      if (!this.isBusy) this.addLoggingToNode(lastConditionNode);

      this.toggleVisibilityOfAdditionalElementsFromDefaultCondition(
        branch,
        firstCondition.id
      );
      this.updateBranch(branch, "error", "exceptionGroups");
      this.refreshElementHeightAndPosition(branch, initialHeight);

      let field = payload?.node?.attrs?.data?.error.find(
        el => el.name === "exceptionMessage"
      );
      let index = payload?.node?.attrs?.data?.error
        .map(f => f.name)
        .indexOf(field);
      let hash = generateHash(20, "we");

      // add the exception message variable to the WFD
      bus.$emit("update-custom-variable-value", {
        id: hash + "-" + index,
        field: field,
        prefix: "error"
      });
    },
    /**
     * Remove every condition/exception group when deactivating the explicit error handling
     * @param payload
     */
    onDeactivateExplicitErrorHandling(payload) {
      let branch = payload.node.getParent();
      let initialHeight = this.getNodeHeight(payload.node, true);
      let condition = payload.node.attrs.data.error
        .find(el => el.name === "exceptionGroups")
        .value.find(val => val.first);
      let existingConditions = branch
        .getChildren()
        .filter(
          el =>
            el.attrs.id !== "condition-" + condition.id &&
            el.attrs.name === "condition"
        );
      for (let existingCondition of existingConditions) {
        this.removeCondition(branch, existingCondition.id().split("-")[1]);
      }

      this.toggleVisibilityOfAdditionalElementsFromDefaultCondition(
        branch,
        condition.id
      );
      this.updateBranch(branch, "error", "exceptionGroups");
      this.refreshElementHeightAndPosition(branch, initialHeight);
    },
    /**
     * check if the given node has (non default) exception branches
     * @param node
     */
    nodeHasExceptionBranches(node) {
      let exceptionBranches = node?.attrs?.data?.error.find(
        el => el.name === "exceptionGroups"
      ).value;
      let exceptionBranchesValues = [];

      if (exceptionBranches) {
        exceptionBranchesValues = exceptionBranches.filter(el => !el.default);
      }

      return exceptionBranchesValues.length > 0;
    },
    /**
     * toggle the visibility of every unnecessary element (line bottom, connector line, label group) from the default exception condition
     * @param branch
     * @param id
     */
    toggleVisibilityOfAdditionalElementsFromDefaultCondition(branch, id) {
      const node = this.getNodeFromGroup(branch);
      const defaultCondition = branch
        .getChildren()
        .find(el => el.attrs.id === "condition-" + id);
      const visible = this.isExplicitErrorHandlingActive(node);
      const isEndpoint = node.attrs.data.is_endpoint;
      const values = isEndpoint
        ? ["top", "bottom", "connectorLine", "label-group"]
        : ["bottom", "connectorLine", "label-group"];

      const childrenToToggle = defaultCondition
        .getChildren()
        .filter(el => values.includes(el.attrs.name));
      childrenToToggle.forEach(childToToggle => childToToggle.visible(visible));

      if (isEndpoint) {
        const connectorLines = this.getConnectorLineFromNode(
          branch,
          branch.attrs.id
        );
        connectorLines.forEach(connectorLine =>
          connectorLine.visible(!isEndpoint)
        );
      }
    },
    /**
     * refresh the height of the current element
     * additionally refresh the position of the following elements
     * @param group
     * @param initialHeight
     */
    refreshElementHeightAndPosition(group, initialHeight = 0) {
      let node = this.getNodeFromGroup(group);
      let nextHash = node?.attrs?.data?.next_hash;

      if (!nextHash) return;

      this.moveNodesBelow(
        node,
        group.getParent(),
        this.getNodeHeight(node) - initialHeight
      );
    },
    /**
     * refreshes the label on all current child branches
     * @param payload
     */
    onRefreshBranchLabel(payload) {
      let branch = payload.node.getParent();
      let conditions = payload.node.attrs.data[payload.dataKey].find(
        el => el.name === payload.elementName
      ).value;

      for (let condition of conditions) {
        let conditionGroup = branch
          .getChildren()
          .find(
            child =>
              child.getClassName() === "Group" &&
              child.id() === "condition-" + condition.id
          );

        let labelGroup = conditionGroup.findOne(".label-group");
        let labelDescription = labelGroup.findOne(".label-description");
        labelDescription.text(resolveLocalizedString(condition.label));
      }
    },
    /**
     * add logging workflow element to given node
     * @param node
     * @returns {Promise<void>}
     */
    async addLoggingToNode(node) {
      let parentNode = this.getNodeFromGroup(node.getParent());
      this.toggleExplicitErrorHandlingCheckbox(parentNode, true);

      let dropzone = node
        .getChildren()
        .find(el => el.attrs.name === "dropzone");
      let dropPosition = {
        position: {
          x: dropzone.x(),
          y: dropzone.y()
        },
        parent: dropzone
      };
      let data = {
        active: true,
        is_endpoint: true,
        config: {
          input: {
            log_message: "{{error.defaultException.message}}"
          },
          configuration: {
            log_method: "log"
          }
        }
      };
      let element = this.library.find(el => el.name === "logging");
      await FlowElements.getConfig({ flowElement: element.name }).then(
        ({ data }) => {
          element.config = data;
        }
      );

      await this.addElement(element, dropPosition, data).then(response => {
        if (response) {
          this.toggleExplicitErrorHandlingCheckbox(parentNode, false);
        }
      });
    },
    /**
     * disable the explicit error handling checkbox, if there is more than one child
     * @param payload
     */
    onOpenErrorConfiguration(payload) {
      let group = payload.node.getParent();

      // get the last default condition ("catch all")
      let lastCondition = payload.node.attrs.data.error
        .find(el => el.name === "exceptionGroups")
        ?.value.find(val => val.last);
      // get every existing additional conditions
      let existingConditions = payload.node.attrs.data.error
        .find(el => el.name === "exceptionGroups")
        ?.value.filter(val => !val.default);
      if (!lastCondition && !existingConditions) return;
      let condition = group.findOne("#condition-" + lastCondition.id);
      let children = condition
        ?.getChildren()
        .filter(el => this.nodeGroups.includes(el?.attrs?.name));
      let disableCheckbox =
        existingConditions.length > 0 || children?.length > 1;

      this.toggleExplicitErrorHandlingCheckbox(payload?.node, disableCheckbox);
    },
    /**
     * enable or disable the explicit error handling checkbox
     * @param node
     * @param disable
     */
    toggleExplicitErrorHandlingCheckbox(node, disable = true) {
      let errorConfig = node?.attrs?.data?.error.find(
        el => el.name === "explicitErrorHandling"
      );
      errorConfig.disabled = disable;
    }
  }
};
</script>

<style lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.node-states {
  i {
    font-size: 1.4rem;
  }
}

#konva-preview {
  bottom: 80px;
  right: 16px;
  z-index: 1;
}

#editor-modal {
  .modal-dialog {
    max-width: 95%;
  }

  .mapping-wrapper {
    max-height: calc(100vh - 140px);
  }

  &.modal-code {
    .modal-dialog {
      height: 95vh;

      .modal-content {
        height: 100%;

        .editor {
          height: 80vh;
          overflow-y: auto;

          .prism-editor__container {
            height: auto;
          }
        }
      }
    }
  }
}
</style>

<style lang="css">
.noteContent {
  overflow: hidden;
  resize: both;
}

.noteEditIcon {
  width: 33px;
  height: 33px;
  font-size: 22px;
  color: #b5b5c3;
}

.noteElementDraggable {
  z-index: 200;
  position: fixed;
}

.noteElementDraggable.start {
  cursor: grabbing;
  transition: 0.1s backdrop-filter ease, 0.3s background ease-out;
  -webkit-transition: 0.1s backdrop-filter ease, 0.3s background ease-out;
  -moz-transition: 0.1s backdrop-filter ease, 0.3s background ease-out;
}

.noteElementDraggable.stop {
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  transition: 1s backdrop-filter ease, 0.3s background ease-out;
  -webkit-transition: 1s backdrop-filter ease, 0.3s background ease-out;
  -moz-transition: 1s backdrop-filter ease, 0.3s background ease-out;
}

.noteContent img {
  width: 100%;
}

.context-menu-default {
  position: absolute;
  width: 320px;
  padding: 0;
  margin: 0;
  background: white;
  z-index: 100;
  border-radius: 12px;
  -webkit-box-shadow: rgba(40, 45, 54, 0.1) 0 16px 48px;
  box-shadow: rgba(40, 45, 54, 0.1) 0 16px 48px;
  opacity: 0;
  -webkit-transform: translate(0, 15px) scale(0.95);
  transform: translate(0, 15px) scale(0.95);
  -webkit-transition: opacity 0.1s ease-out, -webkit-transform 0.1s ease-out;
  transition: opacity 0.1s ease-out, -webkit-transform 0.1s ease-out;
  transition: transform 0.1s ease-out, opacity 0.1s ease-out;
  transition: transform 0.1s ease-out, opacity 0.1s ease-out,
    -webkit-transform 0.1s ease-out;
  pointer-events: none;
}

.context-menu-default-item {
  display: block;
  position: relative;
  margin: 0;
  padding: 0;
  white-space: nowrap;
}

.context-menu-default-btn {
  background: none;
  line-height: normal;
  overflow: visible;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  display: block;
  width: 100%;
  color: #444;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  padding: 8px 16px;
}

.context-menu-default-btn::-moz-focus-inner,
.context-menu-default-btn::-moz-focus-inner {
  border: 0;
  padding: 0;
}

.context-menu-default-icon {
  font-size: 16px;
}

.context-menu-default-text {
  font-size: 14px;
  margin-left: 15px;
}

.context-menu-default-item:hover > .context-menu-default-btn {
  outline: none;
  background-color: #fafafa;
}

.context-menu-default-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.context-menu-default-item.disabled .context-menu-default-btn {
  cursor: default;
}

.context-menu-default-separator {
  display: block;
  margin: 7px 5px;
  height: 1px;
  border-bottom: 1px solid #fff;
  background-color: #aaa;
}

.context-menu-default-item.subcontext-menu-default::after {
  content: "";
  position: absolute;
  right: 6px;
  top: 50%;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-left-color: #808080;
}

.context-menu-default-item.subcontext-menu-default:hover::after {
  border-left-color: #fff;
}

.context-menu-default .context-menu-default {
  top: 4px;
  left: 99%;
}

.show-context-menu-default,
.context-menu-default-item:hover > .context-menu-default {
  opacity: 1;
  -webkit-transform: translate(0, 0) scale(1);
  transform: translate(0, 0) scale(1);
  pointer-events: auto;
}

.context-menu-default-item:hover > .context-menu-default {
  -webkit-transition-delay: 100ms;
  transition-delay: 300ms;
}

/* ============================================================================================ */
/* ======================================= BLUE THEME ========================================= */
/* ============================================================================================ */

.context-menu-blue {
  position: absolute;
  width: 250px;
  padding: 0;
  margin: 0;
  background: #23aff7;
  background: linear-gradient(
    to bottom,
    #23aff7 0%,
    #007bff 100px,
    #007bff 100%
  );
  z-index: 100;
  border-radius: 2px;
  -webkit-box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
  opacity: 0;
  -webkit-transform: translate(0, 15px) scale(0.95);
  transform: translate(0, 15px) scale(0.95);
  -webkit-transition: opacity 0.1s ease-out, -webkit-transform 0.1s ease-out;
  transition: opacity 0.1s ease-out, -webkit-transform 0.1s ease-out;
  transition: transform 0.1s ease-out, opacity 0.1s ease-out;
  transition: transform 0.1s ease-out, opacity 0.1s ease-out,
    -webkit-transform 0.1s ease-out;
  pointer-events: none;
}

.context-menu-blue-item {
  display: block;
  position: relative;
  margin: 0;
  padding: 0;
  white-space: nowrap;
}

.context-menu-blue-btn {
  background: none;
  line-height: normal;
  overflow: visible;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  display: block;
  width: 100%;
  font-family: "Inter", sans-serif;
  text-align: left;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  padding: 1rem 2rem;
}

.context-menu-blue-btn::-moz-focus-inner,
.context-menu-blue-btn::-moz-focus-inner {
  border: 0;
  padding: 0;
}

.context-menu-blue-icon {
  color: white;
  font-size: 1.1rem;
}

.context-menu-blue-text {
  color: white;
  font-family: "Barlow", sans-serif;
  font-size: 1rem;
  margin-left: 20px;
}

.context-menu-blue-item:hover > .context-menu-blue-btn {
  color: #23aff7;
  outline: none;
  background-color: #73d0ff;
  background: -webkit-gradient(
    linear,
    left top,
    left bottom,
    from(#73d0ff),
    to(#73d0ff)
  );
  background: linear-gradient(to bottom, #73d0ff, #73d0ff);
}

.context-menu-blue-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.context-menu-blue-separator {
  display: block;
  margin: 7px 5px;
  height: 1px;
  border-bottom: 1px solid #23aff7;
  background-color: #aaa;
}

.context-menu-blue-item.subcontext-menu-blue::after {
  content: "";
  position: absolute;
  right: 6px;
  top: 50%;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-left-color: #808080;
}

.context-menu-blue-item.subcontext-menu-blue:hover::after {
  border-left-color: #23aff7;
}

.context-menu-blue .context-menu-blue {
  top: 4px;
  left: 99%;
}

.show-context-menu-blue,
.context-menu-blue-item:hover > .context-menu-blue {
  opacity: 1;
  -webkit-transform: translate(0, 0) scale(1);
  transform: translate(0, 0) scale(1);
  pointer-events: auto;
}

.context-menu-blue-item:hover > .context-menu-blue {
  -webkit-transition-delay: 100ms;
  transition-delay: 300ms;
}
</style>
